import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { sysConfig } from '../sysConfig';
import { config } from '../config';
import { ActivationReadinessService } from './activation-readiness.service';
import { trackProductEvent } from '../utils/product-analytics.util';
import { TokenEncryptionService } from '../Services/token-encryption.service';
import type { TrustFirstPurposeId } from './trust-first-copy';
import {
  TRUST_FIRST_PURPOSES,
  buildTrustFirstAckTemplate,
  buildTrustFirstAnnouncement,
  buildTrustFirstAnnounceFacts,
  buildTrustFirstFaq,
  formatFaqForCopy,
  purposeAnnouncementClause,
  type TrustFirstCollectionSnapshot,
} from './trust-first-copy';

export type { TrustFirstPurposeId } from './trust-first-copy';
export { TRUST_FIRST_PURPOSES } from './trust-first-copy';

export type ActivationStepId =
  | 'goal_selected'
  | 'plan_viewed'
  | 'collection'
  | 'mode_education'
  | 'private_test'
  | 'private_proof'
  | 'setup_confirmed'
  | 'announce'
  | 'first_employee'
  | 'first_day_review';

/** Maps loosely to features_interested-style intents. */
export type ActivationGoalId =
  | 'understand_team_work'
  | 'catch_issues_early'
  | 'prove_compliance'
  | 'reduce_idle_waste'
  /** Trust-first purpose chips (preferred for new journeys). */
  | 'work_hours_workload'
  | 'fair_delivery_followups'
  | 'remote_hybrid_support'
  | 'other';

export type InstallRecoveryBranch =
  | 'not_opened'
  | 'uac_denied'
  | 'av_blocked'
  | 'pending'
  | 'continue_later';

export type ActivationStatusPhase =
  | 'package_prepared'
  | 'installed_locally'
  | 'auth_heartbeat'
  | 'signaling'
  | 'first_data'
  | 'needs_attention';

/** Analytics-stable routes. Map to customer paths: invite = employee_runs; computer setup = hero_installs | it_bat. */
export type ActorRoute = 'hero_installs' | 'employee_runs' | 'it_bat';

/** Customer-facing setup method (UI). Maps onto ActorRoute without renaming analytics IDs. */
export type SetupMethod = 'invite' | 'computer_setup';

/** Founder-facing progress chrome (trust-first chapters). */
export type JourneyChapterId =
  | 'purpose'
  | 'collection'
  | 'access'
  | 'announce'
  | 'invite'
  | 'review';

export type JourneyStatus = 'active' | 'paused' | 'completed' | 'skipped';
export type EvaluationStatus = 'inactive' | 'private_active' | 'complete';
export type WaitingReason =
  | null
  | 'install'
  | 'heartbeat'
  | 'screenshot'
  | 'employee_connection'
  | 'employee_data'
  | 'daily_insight';
export type ResumeDestination =
  | null
  | 'ready'
  | 'understand'
  | 'purpose'
  | 'collection'
  | 'access'
  | 'announce'
  | 'install'
  | 'screenshot'
  | 'explore'
  | 'confirm'
  | 'first_employee'
  | 'review';
export type PrivateTestLifecycle =
  | 'none'
  | 'active'
  | 'keep_pending'
  | 'delete_pending'
  | 'archived';

export interface ActivationAnalyticsEvent {
  name: string;
  payload?: Record<string, unknown>;
  at: number;
}

export interface ActivationJourneyState {
  schemaVersion: 3;
  journeyStatus: JourneyStatus;
  evaluationStatus: EvaluationStatus;
  waitingReason: WaitingReason;
  pausedAtStep: ActivationStepId | null;
  pausedAt: string | null;
  resumeDestination: ResumeDestination;
  lastUsefulRoute: string | null;
  privateTestLifecycle: PrivateTestLifecycle;
  currentStep: ActivationStepId;
  completedSteps: ActivationStepId[];
  goal: ActivationGoalId | null;
  mode: string | null;
  modeEducationSeen: boolean;
  privateTestEmployeeId: number | null;
  privateTestEmail: string | null;
  /** Never persisted — kept null in stored blobs. */
  privateTestPassword: string | null;
  installRecoveryBranch: InstallRecoveryBranch | null;
  activationStatusPhase: ActivationStatusPhase;
  firstEmployeeId: number | null;
  firstEmployeeMode: string | null;
  actorRoute: ActorRoute | null;
  /** Keep or remove private-test captures before first employee. */
  keepPrivateTestData: boolean | null;
  /** Founder chose to keep testing privately after the first screenshot. */
  privateEvaluationActive: boolean;
  /** Compatibility flag; prefer journeyStatus === 'paused' for decisions. */
  continuedLater: boolean;
  finished: boolean;
  skipped: boolean;
  emailOtpTracked: boolean;
  analytics: ActivationAnalyticsEvent[];
  /** Explore-guide stops visited: screenshots | apps | hours | work_replay | example_workday | example_trends */
  exploreVisited: string[];
  /** When private test identity / evaluation began — for own-workday ready gate. */
  privateTestStartedAt: string | null;
  /** Explore guide card collapsed to pill after first orientation. */
  exploreGuideCollapsed: boolean;
  /**
   * Founder opened post-proof “what this proves” and/or common concerns.
   * Fair-setup can be skipped once this is true (or after setup_confirmed completes).
   */
  fairSetupAckSeen: boolean;
  /**
   * Phone/tablet Ready chose invite-one-person instead of private test on this computer.
   * Destination must not bounce them back to `/setup/ready` for lack of privateTestEmployeeId.
   */
  inviteFirstFromReady: boolean;
  /**
   * When the founder clicked Download and we started watching for online.
   * Persisted so refresh does not restart the wait clock.
   */
  connectWaitStartedAt: number | null;
  /** One-time tray-icon teach after private-test screenshot. */
  visibilitySwitchTaught: boolean;
  /** One-time tray-icon nudge when first real Visible employee connects. */
  employeeVisibilityNudgeShown: boolean;
  /** One-time “about two minutes” hold when Continue later is clicked before connect. */
  privateTestExitNudgeShown: boolean;
  /** Free text when purpose chip is “Something else”. */
  purposeOtherText: string | null;
  /** Pilot turned tray/Visible off — invite warn. */
  pilotTrayOff: boolean;
  /** Live view allowed for this pilot (announcement + FAQ). */
  pilotLiveViewAllowed: boolean;
  /** Screenshots on for this pilot (mirrors settings; kept for announcement regen). */
  pilotScreenshotsOn: boolean;
  pilotScreenshotIntervalMinutes: number | null;
  /** Apps/websites tracking on for pilot. */
  pilotAppsOn: boolean;
  /** Employee-controlled (true) vs admin-controlled (false). */
  pilotEmployeeControlled: boolean;
  /** Shift label for announcement schedule sentence. */
  pilotShiftLabel: string | null;
  announcementShared: boolean;
  announcementSkippedReason: string | null;
  /** Admin hand-edited the draft; stop auto-regen until reset. */
  announcementDraftEdited: boolean;
  announcementCustomText: string | null;
  inviteWarnDismissed: boolean;
  /**
   * Founder chose “invite an employee” from the see-it fork.
   * The team note is only shown on that path.
   */
  wantsEmployeeInvite: boolean;
}

const ACTIVATION_SCHEMA_VERSION = 3 as const;

const JOURNEY_STATUS_VALUES: JourneyStatus[] = ['active', 'paused', 'completed', 'skipped'];
const EVALUATION_STATUS_VALUES: EvaluationStatus[] = ['inactive', 'private_active', 'complete'];
const WAITING_REASON_VALUES: Array<Exclude<WaitingReason, null>> = [
  'install',
  'heartbeat',
  'screenshot',
  'employee_connection',
  'employee_data',
  'daily_insight',
];
const RESUME_DESTINATION_VALUES: Array<Exclude<ResumeDestination, null>> = [
  'ready',
  'understand',
  'purpose',
  'collection',
  'access',
  'announce',
  'install',
  'screenshot',
  'explore',
  'confirm',
  'first_employee',
  'review',
];
const PRIVATE_TEST_LIFECYCLE_VALUES: PrivateTestLifecycle[] = [
  'none',
  'active',
  'keep_pending',
  'delete_pending',
  'archived',
];
const ACTIVATION_STEP_IDS: ActivationStepId[] = [
  'goal_selected',
  'plan_viewed',
  'collection',
  'mode_education',
  'private_test',
  'private_proof',
  'setup_confirmed',
  'announce',
  'first_employee',
  'first_day_review',
];

function isActivationStepId(value: unknown): value is ActivationStepId {
  return typeof value === 'string' && (ACTIVATION_STEP_IDS as string[]).includes(value);
}

function sanitizeLastUsefulRoute(route: unknown): string | null {
  if (typeof route !== 'string' || !route.startsWith('/') || route.startsWith('//')) {
    return null;
  }
  // Internal app paths only — reject protocol-relative / absolute URLs.
  if (/^[a-z][a-z0-9+.-]*:/i.test(route)) {
    return null;
  }
  return route.slice(0, 512);
}

/** Derive pause resume destination from the step where the founder left. */
export function resumeDestinationForStep(step: ActivationStepId | null | undefined): ResumeDestination {
  switch (step) {
    case 'goal_selected':
      return 'ready';
    case 'plan_viewed':
      return 'purpose';
    case 'collection':
      return 'collection';
    case 'mode_education':
    case 'private_test':
      return 'install';
    case 'private_proof':
      return 'screenshot';
    case 'setup_confirmed':
      return 'access';
    case 'announce':
      return 'announce';
    case 'first_employee':
      return 'first_employee';
    case 'first_day_review':
      return 'review';
    default:
      return 'ready';
  }
}

export function waitingReasonForStep(step: ActivationStepId | null | undefined): WaitingReason {
  switch (step) {
    case 'mode_education':
    case 'private_test':
      return 'install';
    case 'private_proof':
      return 'screenshot';
    case 'first_employee':
      return 'employee_connection';
    case 'first_day_review':
      return 'employee_data';
    default:
      return null;
  }
}

/**
 * After “Done for now” on a live first employee: the hire step is already complete
 * and SuperSee is collecting. Distinct from Continue later before connection.
 */
export function isFirstEmployeeCollectingPause(
  state: Pick<ActivationJourneyState, 'completedSteps' | 'waitingReason'> | null | undefined
): boolean {
  if (!state) {
    return false;
  }
  return (
    (state.completedSteps || []).includes('first_employee') &&
    state.waitingReason === 'employee_data'
  );
}

/**
 * First hire is already connected. Login / guards must not keep reopening the
 * “First connection live” status page — collecting is background work.
 */
export function isFirstEmployeeAlreadyLive(
  state: Pick<
    ActivationJourneyState,
    | 'firstEmployeeId'
    | 'currentStep'
    | 'completedSteps'
    | 'waitingReason'
    | 'activationStatusPhase'
  > | null | undefined
): boolean {
  if (!state?.firstEmployeeId) {
    return false;
  }
  if (isFirstEmployeeCollectingPause(state)) {
    return true;
  }
  if ((state.completedSteps || []).includes('first_employee')) {
    return true;
  }
  if (state.currentStep !== 'first_employee') {
    return false;
  }
  return (
    state.activationStatusPhase === 'auth_heartbeat' ||
    state.activationStatusPhase === 'signaling'
  );
}

/** Skip Access (setup_confirmed) when already completed once, or proof fairness was opened. */
export function shouldSkipFairSetup(
  state: Pick<ActivationJourneyState, 'completedSteps' | 'fairSetupAckSeen'> | null | undefined
): boolean {
  if (!state) {
    return false;
  }
  if ((state.completedSteps || []).includes('setup_confirmed')) {
    return true;
  }
  return state.fairSetupAckSeen === true;
}

/** Announce is done when shared, skipped with reason, or step completed. */
export function shouldSkipAnnounce(
  state: Pick<
    ActivationJourneyState,
    | 'completedSteps'
    | 'announcementShared'
    | 'announcementSkippedReason'
    | 'fairSetupAckSeen'
  > | null | undefined
): boolean {
  if (!state) {
    return false;
  }
  const done = state.completedSteps || [];
  if (done.includes('announce')) {
    return true;
  }
  if (state.announcementShared === true) {
    return true;
  }
  if (state.announcementSkippedReason && String(state.announcementSkippedReason).trim()) {
    return true;
  }
  // Legacy private-proof journeys never saw Collection/Announce.
  if (
    done.includes('private_proof') &&
    !done.includes('collection') &&
    (done.includes('setup_confirmed') || state.fairSetupAckSeen === true)
  ) {
    return true;
  }
  return false;
}

/** Purpose + Collection + Access complete (or access skippable via legacy fair-setup ack). */
export function trustFirstCoreBeforeInviteDone(
  state: Pick<ActivationJourneyState, 'completedSteps' | 'fairSetupAckSeen' | 'goal'> | null | undefined
): boolean {
  if (!state) {
    return false;
  }
  const done = new Set(state.completedSteps || []);
  const purposeOk = done.has('plan_viewed') || !!state.goal;
  const collectionOk = done.has('collection');
  const accessOk = done.has('setup_confirmed') || state.fairSetupAckSeen === true;
  return purposeOk && collectionOk && accessOk;
}

/**
 * Normalize legacy / partial activation blobs to schema v2.
 * Never keeps privateTestPassword. Reconciles finished/skipped/continuedLater/privateEvaluationActive.
 */
export function migrateActivationJourneyState(
  raw: Partial<ActivationJourneyState> | Record<string, unknown> | null | undefined
): ActivationJourneyState {
  const base = defaultState();
  const incoming = raw && typeof raw === 'object' ? (raw as Partial<ActivationJourneyState>) : {};
  const merged: ActivationJourneyState = {
    ...base,
    ...incoming,
    schemaVersion: ACTIVATION_SCHEMA_VERSION,
    completedSteps: Array.isArray(incoming.completedSteps)
      ? incoming.completedSteps.filter(isActivationStepId)
      : base.completedSteps,
    exploreVisited: Array.isArray(incoming.exploreVisited)
      ? incoming.exploreVisited
          .filter((v): v is string => typeof v === 'string')
          .map((v) => (v === 'own_workday' ? 'work_replay' : v))
          .filter((v, i, arr) => arr.indexOf(v) === i)
          .slice(0, 50)
      : base.exploreVisited,
    analytics: Array.isArray(incoming.analytics) ? incoming.analytics.slice(-200) : base.analytics,
    privateTestPassword: null,
    lastUsefulRoute: sanitizeLastUsefulRoute(incoming.lastUsefulRoute),
    fairSetupAckSeen: incoming.fairSetupAckSeen === true,
    inviteFirstFromReady: incoming.inviteFirstFromReady === true,
    visibilitySwitchTaught: incoming.visibilitySwitchTaught === true,
    employeeVisibilityNudgeShown: incoming.employeeVisibilityNudgeShown === true,
    privateTestExitNudgeShown: incoming.privateTestExitNudgeShown === true,
    purposeOtherText:
      typeof incoming.purposeOtherText === 'string' ? incoming.purposeOtherText.slice(0, 120) : null,
    pilotTrayOff: incoming.pilotTrayOff === true,
    // Screenshots and Live view start ON; only an explicit false turns them off.
    pilotLiveViewAllowed: incoming.pilotLiveViewAllowed !== false,
    pilotScreenshotsOn: incoming.pilotScreenshotsOn !== false,
    pilotScreenshotIntervalMinutes:
      typeof incoming.pilotScreenshotIntervalMinutes === 'number'
        ? incoming.pilotScreenshotIntervalMinutes
        : null,
    pilotAppsOn: incoming.pilotAppsOn !== false,
    pilotEmployeeControlled: incoming.pilotEmployeeControlled !== false,
    pilotShiftLabel: typeof incoming.pilotShiftLabel === 'string' ? incoming.pilotShiftLabel : null,
    announcementShared: incoming.announcementShared === true,
    announcementSkippedReason:
      typeof incoming.announcementSkippedReason === 'string'
        ? incoming.announcementSkippedReason.slice(0, 240)
        : null,
    announcementDraftEdited: incoming.announcementDraftEdited === true,
    announcementCustomText:
      typeof incoming.announcementCustomText === 'string' ? incoming.announcementCustomText : null,
    inviteWarnDismissed: incoming.inviteWarnDismissed === true,
    wantsEmployeeInvite: incoming.wantsEmployeeInvite === true,
  };

  const waitAt = Number(incoming.connectWaitStartedAt);
  merged.connectWaitStartedAt = Number.isFinite(waitAt) && waitAt > 0 ? waitAt : null;

  if (!isActivationStepId(merged.currentStep)) {
    merged.currentStep = 'goal_selected';
  }
  if (merged.pausedAtStep != null && !isActivationStepId(merged.pausedAtStep)) {
    merged.pausedAtStep = null;
  }

  // Legacy → canonical status (order matters: finished/skipped beat pause/eval).
  if (merged.finished === true) {
    merged.journeyStatus = 'completed';
    merged.evaluationStatus = 'complete';
  } else if (merged.skipped === true) {
    merged.journeyStatus = 'skipped';
  } else if (merged.privateEvaluationActive === true) {
    merged.journeyStatus = 'active';
    merged.evaluationStatus = 'private_active';
    // Split the old contradictory continuedLater+privateEvaluation pairing.
    merged.continuedLater = false;
  } else if (merged.continuedLater === true) {
    merged.journeyStatus = 'paused';
  } else if (
    incoming.journeyStatus &&
    JOURNEY_STATUS_VALUES.includes(incoming.journeyStatus as JourneyStatus)
  ) {
    merged.journeyStatus = incoming.journeyStatus as JourneyStatus;
  } else {
    merged.journeyStatus = 'active';
  }

  if (!EVALUATION_STATUS_VALUES.includes(merged.evaluationStatus)) {
    merged.evaluationStatus = merged.privateEvaluationActive ? 'private_active' : 'inactive';
  }
  if (merged.waitingReason != null && !WAITING_REASON_VALUES.includes(merged.waitingReason)) {
    merged.waitingReason = null;
  }
  if (
    merged.resumeDestination != null &&
    !RESUME_DESTINATION_VALUES.includes(merged.resumeDestination)
  ) {
    merged.resumeDestination = null;
  }
  if (!PRIVATE_TEST_LIFECYCLE_VALUES.includes(merged.privateTestLifecycle)) {
    merged.privateTestLifecycle = merged.privateTestEmployeeId ? 'active' : 'none';
  } else if (merged.privateTestLifecycle === 'none' && merged.privateTestEmployeeId) {
    merged.privateTestLifecycle = 'active';
  }

  if (merged.journeyStatus === 'paused') {
    if (!merged.pausedAtStep) {
      merged.pausedAtStep = merged.currentStep;
    }
    if (!merged.pausedAt) {
      merged.pausedAt = new Date().toISOString();
    }
    if (!merged.resumeDestination) {
      merged.resumeDestination = resumeDestinationForStep(merged.pausedAtStep || merged.currentStep);
    }
    if (merged.waitingReason == null) {
      merged.waitingReason = waitingReasonForStep(merged.pausedAtStep || merged.currentStep);
    }
  }

  // Legacy private-proof journeys never had collection/announce — soft-complete those steps
  // so resume does not bounce founders back to Purpose.
  const done = new Set(merged.completedSteps || []);
  if (
    done.has('private_proof') &&
    (done.has('setup_confirmed') || merged.fairSetupAckSeen) &&
    !done.has('announce')
  ) {
    ['plan_viewed', 'collection', 'setup_confirmed', 'announce'].forEach((id) => done.add(id as ActivationStepId));
    merged.completedSteps = Array.from(done);
    if (!merged.announcementShared && !merged.announcementSkippedReason) {
      merged.announcementSkippedReason = 'legacy_pre_trust_first';
    }
  }

  return merged;
}

function pickEarlierTimestamp(a: number | null | undefined, b: number | null | undefined): number | null {
  const first = Number.isFinite(Number(a)) && Number(a) > 0 ? Number(a) : null;
  const second = Number.isFinite(Number(b)) && Number(b) > 0 ? Number(b) : null;
  if (first != null && second != null) {
    return Math.min(first, second);
  }
  return first ?? second;
}

/** Exported for unit tests and hydrateFromServer merge. */
export function mergePreferringFurtherAlong(
  local: ActivationJourneyState,
  server: ActivationJourneyState
): ActivationJourneyState {
  const order = ACTIVATION_STEPS.map((s) => s.id);
  const localMigrated = migrateActivationJourneyState(local);
  const serverMigrated = migrateActivationJourneyState(server);
  const localIdx = Math.max(0, order.indexOf(localMigrated.currentStep));
  const serverIdx = Math.max(0, order.indexOf(serverMigrated.currentStep));
  const localDone = localMigrated.completedSteps?.length || 0;
  const serverDone = serverMigrated.completedSteps?.length || 0;
  // Deliberate collecting pause: completed first_employee, still on first_employee, waiting for data.
  // Do not let a premature first_day_review step erase that pause.
  const localCollectingPause =
    localMigrated.waitingReason === 'employee_data' &&
    localMigrated.currentStep === 'first_employee' &&
    (localMigrated.completedSteps || []).includes('first_employee') &&
    serverMigrated.currentStep === 'first_day_review' &&
    !(serverMigrated.completedSteps || []).includes('first_day_review');
  const localAhead =
    localCollectingPause ||
    localIdx > serverIdx ||
    (localIdx === serverIdx && localDone > serverDone);
  const connectWaitStartedAt = pickEarlierTimestamp(
    localMigrated.connectWaitStartedAt,
    serverMigrated.connectWaitStartedAt
  );

  if (localAhead) {
    return migrateActivationJourneyState({
      ...serverMigrated,
      ...localMigrated,
      privateTestEmployeeId:
        localMigrated.privateTestEmployeeId ?? serverMigrated.privateTestEmployeeId,
      firstEmployeeId: localMigrated.firstEmployeeId ?? serverMigrated.firstEmployeeId,
      firstEmployeeMode: localMigrated.firstEmployeeMode ?? serverMigrated.firstEmployeeMode,
      actorRoute: localMigrated.actorRoute ?? serverMigrated.actorRoute,
      privateTestLifecycle:
        localMigrated.privateTestLifecycle !== 'none'
          ? localMigrated.privateTestLifecycle
          : serverMigrated.privateTestLifecycle,
      analytics: [...(serverMigrated.analytics || []), ...(localMigrated.analytics || [])].slice(
        -200
      ),
      connectWaitStartedAt,
      privateTestExitNudgeShown:
        localMigrated.privateTestExitNudgeShown || serverMigrated.privateTestExitNudgeShown,
      privateTestPassword: null,
    });
  }
  return migrateActivationJourneyState({
    ...localMigrated,
    ...serverMigrated,
    privateTestEmployeeId:
      serverMigrated.privateTestEmployeeId ?? localMigrated.privateTestEmployeeId,
    firstEmployeeId: serverMigrated.firstEmployeeId ?? localMigrated.firstEmployeeId,
    firstEmployeeMode: serverMigrated.firstEmployeeMode ?? localMigrated.firstEmployeeMode,
    actorRoute: serverMigrated.actorRoute ?? localMigrated.actorRoute,
    exploreGuideCollapsed:
      localMigrated.exploreGuideCollapsed || serverMigrated.exploreGuideCollapsed,
    exploreVisited: Array.from(
      new Set([...(serverMigrated.exploreVisited || []), ...(localMigrated.exploreVisited || [])])
    ).slice(0, 50),
    analytics: [...(localMigrated.analytics || []), ...(serverMigrated.analytics || [])].slice(
      -200
    ),
    connectWaitStartedAt,
    privateTestExitNudgeShown:
      localMigrated.privateTestExitNudgeShown || serverMigrated.privateTestExitNudgeShown,
    privateTestPassword: null,
  });
}

export type ExploreStopId =
  | 'screenshots'
  | 'apps'
  | 'hours'
  | 'work_replay'
  | 'example_workday'
  | 'example_trends'
  | 'own_workday';

/**
 * Backend step IDs stay stable for analytics where possible.
 * Trust-first adds collection + announce; setup_confirmed = Access; plan_viewed = Purpose.
 */
export const ACTIVATION_STEPS: Array<{
  id: ActivationStepId;
  label: string;
  description: string;
}> = [
  {
    id: 'goal_selected',
    label: 'Ready',
    description: 'Name your workspace and continue.',
  },
  {
    id: 'plan_viewed',
    label: 'Purpose',
    description: 'Why are you introducing SuperSee?',
  },
  {
    id: 'collection',
    label: 'Collection',
    description: 'Choose what SuperSee records for the pilot.',
  },
  {
    id: 'mode_education',
    label: 'Install',
    description: 'Optional: install SuperSee on this computer.',
  },
  {
    id: 'private_test',
    label: 'Install',
    description: 'Download SuperSee for this computer and wait until it is connected.',
  },
  {
    id: 'private_proof',
    label: 'Screenshot',
    description: 'See your first screenshot.',
  },
  {
    id: 'setup_confirmed',
    label: 'Access',
    description: 'Who can see the team?',
  },
  {
    id: 'announce',
    label: 'Announce',
    description: 'Tell the team before they install.',
  },
  {
    id: 'first_employee',
    label: 'Invite',
    description: 'Start with up to 5 people on the trial pilot.',
  },
  {
    id: 'first_day_review',
    label: 'Review',
    description: 'Open one recorded workday and ask one real question.',
  },
];

/**
 * Public URL slugs under `/setup/:step` for GA funnel paths.
 * Legacy slugs understand/confirm still resolve via activationSlugToStep aliases.
 */
export const ACTIVATION_STEP_SLUGS: Record<ActivationStepId, string> = {
  goal_selected: 'ready',
  plan_viewed: 'purpose',
  collection: 'collection',
  mode_education: 'prepare',
  private_test: 'install',
  private_proof: 'screenshot',
  setup_confirmed: 'access',
  announce: 'announce',
  first_employee: 'first-employee',
  first_day_review: 'review',
};

export const ACTIVATION_SLUG_TITLES: Record<string, string> = {
  ready: 'Ready',
  purpose: 'Purpose',
  understand: 'Purpose',
  collection: 'Collection',
  prepare: 'Prepare',
  install: 'Install',
  screenshot: 'Screenshot',
  access: 'Access',
  confirm: 'Access',
  announce: 'Announce',
  'first-employee': 'Invite',
  review: 'Review',
};

export function activationStepToSlug(stepId: ActivationStepId): string {
  return ACTIVATION_STEP_SLUGS[stepId] || 'ready';
}

export function activationSlugToStep(slug: string | null | undefined): ActivationStepId | null {
  if (!slug) {
    return null;
  }
  // Legacy URL aliases from pre–trust-first journey.
  if (slug === 'understand') {
    return 'plan_viewed';
  }
  if (slug === 'confirm') {
    return 'setup_confirmed';
  }
  const entry = (Object.entries(ACTIVATION_STEP_SLUGS) as Array<[ActivationStepId, string]>).find(
    ([, s]) => s === slug
  );
  return entry ? entry[0] : null;
}

/**
 * Founder-facing progress chrome — trust-first chapters.
 * Optional private test is a sub-flow under Invite, not a bar tick.
 */
export const JOURNEY_CHAPTERS_ALL: Array<{ id: JourneyChapterId; label: string }> = [
  { id: 'purpose', label: 'Workspace' },
  { id: 'collection', label: 'Collection' },
  { id: 'invite', label: 'Preview' },
  { id: 'review', label: 'Review' },
];

/** @deprecated Use JOURNEY_CHAPTERS_ALL — kept for any leftover imports. */
export const JOURNEY_CHAPTERS_A = JOURNEY_CHAPTERS_ALL.slice(0, 2);
/** @deprecated Use JOURNEY_CHAPTERS_ALL — kept for any leftover imports. */
export const JOURNEY_CHAPTERS_B = JOURNEY_CHAPTERS_ALL.slice(2);

export const ACTIVATION_GOALS: Array<{
  id: ActivationGoalId;
  title: string;
  description: string;
  icon: string;
  featuresInterested: string[];
  recommendedMode: 'hidden' | 'employee_controlled' | 'admin_controlled';
  diagnosticNote: string;
  /** Guide reflection reused on later screens after the goal is chosen. */
  reflection: string;
}> = [
  {
    id: 'work_hours_workload',
    title: 'See how the workday actually went',
    description: 'Hours, active time, and where the day went — so a workload talk starts from a record.',
    icon: 'schedule',
    featuresInterested: ['attendance', 'timesheets', 'insights'],
    recommendedMode: 'employee_controlled',
    diagnosticNote: 'Start visible so people can see SuperSee while it runs.',
    reflection: 'You want clearer visibility into work hours and workload.',
  },
  {
    id: 'fair_delivery_followups',
    title: 'Have context when delivery slips',
    description: 'Look at the workday before you follow up. This is context, not a ranking of people.',
    icon: 'forum',
    featuresInterested: ['tracking', 'insights'],
    recommendedMode: 'employee_controlled',
    diagnosticNote: 'Start visible so the first pilot feels fair and open.',
    reflection: 'You want fairer follow-ups when delivery slips.',
  },
  {
    id: 'remote_hybrid_support',
    title: 'Stay close to remote and hybrid days',
    description: 'See the workday without asking people to write a status update.',
    icon: 'home_work',
    featuresInterested: ['tracking', 'screencast'],
    recommendedMode: 'employee_controlled',
    diagnosticNote: 'Start visible so remote teammates know SuperSee is running.',
    reflection: 'You want clearer support for remote or hybrid teams.',
  },
  {
    id: 'other',
    title: 'A different reason',
    description: 'One line. We’ll put it in the note you share with the team.',
    icon: 'edit',
    featuresInterested: ['tracking', 'insights'],
    recommendedMode: 'employee_controlled',
    diagnosticNote: 'Start visible for the pilot.',
    reflection: 'You want clearer visibility into how work is happening.',
  },
  // Legacy goals kept for in-progress journeys that already stored them.
  {
    id: 'understand_team_work',
    title: 'See what work is happening',
    description: 'Manage with evidence, not assumptions — before anyone else installs.',
    icon: 'groups',
    featuresInterested: ['tracking', 'insights'],
    recommendedMode: 'employee_controlled',
    diagnosticNote: 'Start visible so your first employee can confirm tracking feels fair and accurate.',
    reflection: 'You want clearer visibility into what work is happening.',
  },
  {
    id: 'catch_issues_early',
    title: 'Apps and websites in use',
    description: 'See which tools and sites fill the day — without a feature catalogue.',
    icon: 'apps',
    featuresInterested: ['screencast', 'tracking'],
    recommendedMode: 'employee_controlled',
    diagnosticNote: 'Start visible for the pilot.',
    reflection: 'You want clearer visibility into apps and websites in use.',
  },
  {
    id: 'prove_compliance',
    title: 'Attendance and work hours',
    description: 'A clearer record of presence and tracked time — you stay in control of rollout.',
    icon: 'verified_user',
    featuresInterested: ['attendance', 'timesheets'],
    recommendedMode: 'employee_controlled',
    diagnosticNote: 'Start visible for the pilot.',
    reflection: 'You want clearer visibility into attendance and work hours.',
  },
  {
    id: 'reduce_idle_waste',
    title: 'Where work time goes',
    description: 'Find where time drifts — without undefended “productive” scores.',
    icon: 'timer',
    featuresInterested: ['reports', 'insights'],
    recommendedMode: 'employee_controlled',
    diagnosticNote: 'Start visible for the pilot.',
    reflection: 'You want clearer visibility into where work time goes.',
  },
];

/** Purpose chips shown on the Purpose screen (excludes legacy-only goals). */
export const TRUST_FIRST_PURPOSE_GOALS = ACTIVATION_GOALS.filter((g) =>
  (TRUST_FIRST_PURPOSES.map((p) => p.id) as string[]).includes(g.id)
);

/** Customer-facing guide lines — plain founder vocabulary only. */
export const JOURNEY_GUIDE_COPY = {
  focusPromise:
    'See what needs attention—without constantly checking on people.',
  /** How SuperSee enables the promise — one sharp line under the headline. */
  empathyReality:
    'Install SuperSee on this computer first. Invite someone only when you are ready.',
  /** Ready (post-OTP) — confidence hero, not product slogan. */
  readyVerified: 'Verified',
  readyEmailKicker: 'Email verified',
  readyStripKicker: 'Your workspace',
  readyStepLabel: 'Step 1 of 4',
  readyContactPhoneLabel: 'WhatsApp number',
  readyContactPhoneHint: 'Receive a daily summary of team activity on WhatsApp.',
  readyTeamSizeLabel: 'Team size',
  readyWorkspaceLabel: 'Workspace name',
  readyNextCaption: 'Next: Choose why you’re introducing SuperSee',
  proofContactPhoneTitle: 'Get Daily Insights on WhatsApp',
  proofContactPhoneHint:
    'Same short company overview—tracked, absentees, needs attention, distractions—without opening the dashboard.',
  proofContactPhoneSkip: 'Not now',
  fairContactPhoneTitle: 'Get Daily Insights on WhatsApp',
  fairContactPhoneHint:
    'Add your number so we can send the daily company summary on WhatsApp after real workdays start.',
  fairContactPhoneSkip: 'Not now',
  waPopupHeadline: 'Get your Daily Insights on WhatsApp',
  waPopupSupport:
    'A short company snapshot each day—without opening the dashboard. Full roster stays in email.',
  waPopupPreview: `SuperSee Daily Insights · Acme Ops
12 / 15 tracked · Activity 78%
Needs attention: Alex — high idle 41%
Distractions: youtube.com 12m`,
  waPopupCta: 'Save number',
  waPopupSecondary: 'Maybe later',
  waPopupInvalid: 'Enter a valid mobile number for the selected country.',
  readyHeadline: 'Set up your workspace',
  readyBody: 'Use your company or team name.',
  readyDecisionBody: 'Use your company or team name.',
  readyContinueCta: 'Continue',
  understandHeadline: 'Why are you introducing SuperSee?',
  understandBridge: 'Pick the main reason. We’ll use it in the note you share with the team.',
  understandCta: 'Continue',
  understandLookAroundCta: 'Look around first',
  understandTrust: 'Temporary. Only this computer. Remove anytime.',
  purposeHeadline: 'Why are you introducing SuperSee?',
  purposeBody: 'Pick the main reason. It goes in the note you send the team.',
  purposeContinueCta: 'Continue',
  purposeOtherPlaceholder: 'One short line (max 120 characters)',
  collectionHeadline: 'Choose what SuperSee records',
  collectionBody: 'These are the settings people will notice. Change anything that does not fit.',
  collectionNextCta: 'Continue',
  collectionBeatAppsTitle: 'Will you record apps and websites?',
  collectionBeatAppsBody: 'This is the first thing people will notice in a timesheet.',
  collectionAppsOnTitle: 'Yes — apps and websites',
  collectionAppsOnDetail: 'Shows which apps and sites were used. Not message contents.',
  collectionAppsOffTitle: 'No — leave this off',
  collectionAppsOffDetail: 'Timesheets will not list apps or sites.',
  collectionBeatHoursTitle: 'Hours are always included',
  collectionBeatHoursBody:
    'Active time, idle stretches, and tracked hours are always included. Idle can include meetings and calls.',
  collectionBeatShotsTitle: 'Capture screenshots?',
  collectionBeatShotsBody: 'A picture of their screen every few minutes. Only workspace admins can review them.',
  collectionShotsOnTitle: 'Yes, take screenshots',
  collectionShotsOnDetail: 'Recommended. Lets you check a timesheet against real work.',
  collectionShotsOffTitle: 'No screenshots',
  collectionShotsOffDetail: 'No screenshots are taken. The team note will say so.',
  collectionIntervalLabel: 'About every',
  collectionBeatLiveTitle: 'Allow live view?',
  collectionBeatLiveBody: 'A manager can see one screen as it is right now. It is not a recording.',
  collectionLiveOnTitle: 'Yes, when something needs attention',
  collectionLiveOnDetail: 'Recommended. A manager opens one screen at a time to check on something.',
  collectionLiveOffTitle: 'No live view',
  collectionLiveOffDetail: 'Nobody can open a live screen.',
  collectionBeatModeTitle: 'How should SuperSee run by default?',
  collectionBeatModeBody: 'This is your workspace default. You can change it for any employee later.',
  collectionBeatTrayTitle: 'Visible app or silent app?',
  collectionBeatTrayBody: 'Visible shows SuperSee on their computer. Hidden runs in the background.',
  collectionTrayOnTitle: 'Visible app',
  collectionTrayOnDetail: 'They can see SuperSee running on their computer.',
  collectionTrayOffTitle: 'Silent app',
  collectionTrayOffDetail: 'It runs in the background. Say so clearly in the team note.',
  collectionBeatScheduleTitle: 'When does this run?',
  collectionBeatScheduleBody: 'These are the hours already on the workspace.',
  collectionConfirmTitle: 'Check your setup',
  collectionConfirmBody: 'This is what SuperSee will record for your team. You can change any of it later in Settings.',
  collectionConfirmChosen: 'Your choices',
  collectionConfirmAlso: 'Also recorded',
  collectionConfirmEdit: 'Change',
  collectionConfirmShotsLabel: 'Screenshots',
  collectionConfirmShotsEvery: 'About every {n} minutes',
  collectionConfirmModeLabel: 'How tracking runs',
  collectionConfirmAppsDetail: 'Which apps and sites were used. Never message contents.',
  collectionConfirmHoursDetail: 'Active, idle and tracked time. Always on.',
  collectionConfirmLiveDetail: 'A manager can open one screen when something needs a look.',
  collectionConfirmApps: 'Apps and websites',
  collectionConfirmLive: 'Live view',
  collectionConfirmOn: 'On',
  collectionConfirmOff: 'Off',
  collectionConfirmHours: 'Hours',
  collectionConfirmHoursValue: 'Included',
  collectionConfirmSchedule: 'Work hours',
  collectionScheduleSave: 'Update these hours',
  collectionScheduleSaved: 'Hours updated',
  collectionScheduleNoShift: 'Uses the company work schedule. You can set exact hours later in Settings.',
  collectionScheduleStarts: 'Starts',
  collectionScheduleEnds: 'Ends',
  collectionSaveCta: 'Save and continue',
  collectionFooter: '',
  collectionAppsLabel: 'Apps and websites',
  collectionAppsExperience: 'Shows which apps and sites were used. Not message contents.',
  collectionHoursLabel: 'Active, idle, and timesheet hours',
  collectionHoursExperience:
    'Shows active and idle stretches and tracked hours. Idle can include meetings and calls.',
  collectionHoursIncluded: 'Always included',
  collectionScreenshotsLabel: 'Screenshots',
  collectionScreenshotsExperience:
    'A picture of their screen every few minutes. Turn off only if you do not want screenshots.',
  collectionLiveLabel: 'Live view',
  collectionLiveExperience:
    'A manager can open one screen when something needs attention.',
  collectionLiveAuditNote: 'Views can be logged.',
  collectionModeHiddenDetail: 'Runs silently in the background. Nothing on their screen and no sign-in.',
  collectionModeHiddenCaution: 'Use only where company policy allows, and tell your team first.',
  collectionModeRecommendedTag: 'Recommended',
  collectionModeFactSelfStart: 'Employee starts and stops it',
  collectionModeFactAutoStart: 'Starts automatically',
  collectionModeFactVisible: 'Visible on their computer',
  collectionModeFactHidden: 'Not visible on their computer',
  collectionModeLabel: 'How tracking runs',
  collectionModeEmployee: 'Manual — they start and stop it',
  collectionModeEmployeeTitle: 'Manual',
  collectionModeEmployeeDetail: 'Employees start and stop tracking themselves. They can see SuperSee on their computer.',
  collectionModeAdmin: 'Automatic — tracking runs on its own',
  collectionModeAdminTitle: 'Automatic',
  collectionModeAdminDetail: 'Tracking starts on its own and employees can’t stop it. They can still see SuperSee.',
  collectionTrayLabel: 'App visibility',
  collectionTrayExperience: 'Visible shows SuperSee on their computer. Hidden runs in the background.',
  collectionHoursScheduleLabel: 'Work hours',
  collectionHoursScheduleFallback: 'During the company work schedule',
  collectionEditSchedule: 'Edit schedule',
  accessHeadline: 'Who can see the team?',
  accessBody: 'Managers and leads review their own teams.',
  accessEmptyTeams:
    'You can assign managers when you add teams. For now, workspace admins can review the workspace.',
  accessCoaching: 'Use the timeline as context, not as a verdict.',
  accessContinueCta: 'Continue',
  announceHeadline: 'Tell the team before they install',
  announceBody: 'Check the list, then send the note. Edit it if a sentence is wrong.',
  announceFactsTitle: 'What the note says',
  announcePreviewLabel: 'The email',
  announceEditCta: 'Edit',
  announceDoneEditingCta: 'Done',
  announceResetCta: 'Reset to settings',
  announceFaqTitle: 'Questions people may ask',
  announceFaqHint: 'Copy these if someone asks before they install.',
  announceAckTitle: 'Optional: acknowledgement for HR',
  announceAckLabel: 'A starting point for your HR process. It is not a legal document.',
  announceCopyCta: 'Copy note',
  announceCopyFaqCta: 'Copy questions',
  announceCopyAckCta: 'Copy acknowledgement',
  announceEmailCta: 'Open in email',
  announceSharedCta: 'I’ve shared it, continue',
  announceSkipCta: 'Skip for now',
  seeItHeadline: 'See it working on a real computer',
  seeItBody: 'Most teams try SuperSee on their own computer first, then invite people.',
  seeItThisPcTitle: 'Test on this computer',
  seeItThisPcDetail: 'Install SuperSee here and see your first screenshot before anyone else joins.',
  seeItThisPcCta: 'Start the test',
  seeItInviteTitle: 'Invite an employee',
  seeItInviteDetail: 'They install SuperSee on their computer. You share the team note before the invite goes out.',
  seeItInviteCta: 'Invite by email',
  seeItFootnote: 'You can always invite more people later.',
  invitePilotHeadline: 'Invite one person',
  invitePilotBody: '14-day Professional trial. Open the employee form and add them.',
  invitePilotBodyAfterPrivate:
    '14-day Professional trial. You already saw it on this computer. Add one person next.',
  invitePilotCta: 'Invite one person',
  inviteChecklistPurpose: 'Purpose chosen',
  inviteChecklistCollection: 'Collection saved',
  inviteChecklistAccess: 'Access reviewed',
  inviteChecklistAnnounce: 'Announcement shared or skipped with a reason',
  invitePrivateTestCta: 'Install on this computer first',
  inviteWarnTitle: 'Share this with the team first?',
  inviteWarnBody:
    'The invite will go out with the current settings. People should hear what is recorded before they install.',
  inviteWarnTrayOff:
    'Silent app is on, so they may not see SuperSee running. Tell them in the announcement.',
  inviteWarnBack: 'Go back and copy the note',
  inviteWarnAnyway: 'Invite anyway',
  reviewLead: 'Open one recorded workday and ask one real question.',
  reviewOpenCta: 'Open this workday',
  reviewCoaching:
    'Treat timelines as context. Look at workload, process, or unclear expectations before assuming intent.',
  understandRecordsTitle: 'What SuperSee records',
  understandRecords: [
    'Apps and websites',
    'Active and idle time',
    'Timesheets',
    'Screenshots when you turn them on',
    'Live view when you allow it',
  ] as const,
  understandRecordsExclusions:
    'No keystrokes, no passwords, no microphone, no continuous video. Idle can include meetings and calls. Activity is not output.',
  /** @deprecated Ready no longer offers mode radios. */
  readyModeLegend: 'How should this install run?',
  readyModeVisibleTitle: 'Visible',
  readyModeVisibleSupport: 'You’ll see SuperSee while it runs.',
  readyModeHiddenTitle: 'Hidden',
  readyModeHiddenSupport: 'Runs in the background. We’ll confirm connection in this browser.',
  readyModeFootnote:
    'This choice is only for this computer. You choose again for each person.',
  readySteps: [
    {
      title: 'Install SuperSee',
      support: 'Usually takes about two minutes',
    },
    {
      title: 'Open any work screen',
      support: 'Choose something you are comfortable capturing',
    },
    {
      title: 'See your first screenshot',
      support: 'Understand exactly what SuperSee records',
    },
  ] as const,
  readyCta: 'Install on this computer',
  readyTrust: 'Temporary. Only this computer. Remove anytime.',
  readyLookAroundCta: 'Look around first',
  readySampleLink: 'Not on this computer? Explore sample data instead',
  readySampleLinkShort: 'Explore sample data instead',
  /** Install wizard (before download) — evidence first; always tray-visible. */
  installHeadline: 'Install SuperSee on this computer',
  installConfigured:
    'One screenshot shows your screen, the app, and the time. That is the evidence for Work Replay and Daily Insights.',
  installConfiguredHidden:
    'SuperSee runs in the background. You may not see it on the desktop. This page will say when this computer is connected.',
  installModeLater:
    'Tray icon stays on so you can see it. You can turn it off later from admin.',
  installPublisher: 'Publisher: Rao Information Technology Pvt Ltd',
  installTrust: 'Temporary · Only this computer · Remove anytime',
  /** One-line recover link — expands to installAntivirusTip. */
  installAntivirusLink: 'Having trouble? Antivirus may quarantine during install.',
  /** Install-time only — SuperSee usually runs fine once Antivirus is back on. */
  installAntivirusTip:
    'Some Antivirus software quarantines SuperSee during install. Pause Antivirus briefly while you install, then turn Antivirus back on. After setup, SuperSee runs normally with Antivirus on.',
  installOpenTitle: 'Open SuperSee Setup',
  installStepOpenTitle: 'Open the downloaded file',
  installStepOpenLookFor: 'Look for',
  installStepOpenInDownloads: 'in your browser’s downloads.',
  installStepSignInTitle: 'Sign in to the SuperSee app with these',
  installStepSignInNotAdmin: 'These are for the app on this computer, not your admin login.',
  installStepWaitTitle: 'Wait here. We’ll move on by ourselves.',
  installWaitingTitle: 'Waiting for SuperSee',
  installWaitingBody:
    'Usually under a minute after you open the file.',
  installWaitingTitleHidden: 'Waiting for this computer',
  installWaitingBodyHidden: 'No app window is expected. Keep this page open.',
  visibilitySwitchTeach:
    'This ran with the tray icon on, so you could see SuperSee. You can turn that icon off for a computer from the admin panel. It usually shows up in about 10 minutes.',
  visibilitySwitchGotIt: 'Got it',
  visibilitySwitchMaybeLater: 'Maybe later',
  proofAddEmployeeCta: 'Add one employee',
  proofKeepReviewingLink: 'Stay on this computer',
  hireTrayIconHint:
    'On the form, tray icon on means they can see SuperSee. Tray icon off means no icon, only where your company policy allows it.',
  hireFairSetupToggle: 'How to introduce SuperSee fairly',
  hireFairSetupHide: 'Hide introduction guide',
  privateTestLoginHeading: 'Sign in with these details',
  privateTestLoginDetails:
    'If SuperSee asks you to log in, paste these — only for this computer',
  privateTestLoginHint:
    'Use these in the SuperSee app on this computer — not your admin login.',
  privateTestLoginPrepare:
    'Download again to get a password you can copy. Use that new file, not an older download.',
  privateTestLoginShow: 'Login details',
  privateTestLoginHide: 'Hide login details',
  privateTestExitNudgeTitle: 'See it on this computer first',
  privateTestExitNudgeBody:
    'One screenshot shows your screen, the app, and the time — before you ask anyone else to install SuperSee. About two minutes. The tray icon stays on.',
  privateTestExitNudgeStay: "I'll install it now",
  installBrowserKeepHint:
    'Edge or Chrome may ask Keep or Delete. Choose Keep. If it asks again, choose Keep anyway.',
  installDownloadAgain: 'Download again',
  installOpenHelp: 'Need help opening the file?',
  installOpenHelpHide: 'Hide file help',
  installConnectedTitle: 'This computer is connected',
  installConnectedBody:
    'Open a screen you are comfortable capturing. When you are ready, take your first screenshot.',
  installConnectedCta: 'Take a screenshot',
  /** Pre-capture proof stage — headline ≠ button (Krug). */
  proofCaptureTitle: 'Take one screenshot',
  proofCaptureCta: 'Capture screenshot',
  installHelpTitle: 'Taking longer than expected?',
  /** Competence: whole-product outcome — not a claim about one screenshot. */
  productSentence:
    'See the workday as it happened—then decide where to step in.',
  /** Product story stages — functional questions the founder asks. */
  outcomes: [
    {
      id: 'live',
      title: 'See Now',
      question: 'What is happening?',
      blurb:
        'When someone is Active, open Live Monitoring and View their screen in real time—so you know what is happening now.',
      when: 'Available once a computer is connected.',
    },
    {
      id: 'replay',
      title: 'Replay the Day',
      question: 'What happened?',
      blurb:
        'Work Replay reconstructs a day with screenshots, app context, and usage vs idle—so you can see what happened, not only what was reported.',
      when: 'Builds from real work after SuperSee is running.',
    },
    {
      id: 'trends',
      title: 'Spot What Repeats',
      question: 'What keeps happening?',
      blurb:
        'Productivity Trends turns days of work into attention signals and deep-work views—so you can tell a one-off spike from something that keeps happening.',
      when: 'Clearest after days of real use—not from one install on this computer.',
    },
    {
      id: 'act',
      title: 'Find the Way',
      question: 'Where should we act next?',
      blurb:
        'Use the evidence—live, replay, and what repeats—to decide where to step in. SuperSee shows the path; you choose the action.',
      when: 'Grows as your own proof and team usage accumulate.',
    },
  ] as const,
  exampleViewLabel:
    'Example view. Your own data appears after you connect a computer.',
  /** Short responsible-use values — not a privacy lecture. */
  trustCommitments: [
    'A clear window, not a hidden camera.',
    'Attention to protect, not punish.',
    'No daily forms or timesheets after setup.',
  ] as const,
  focusTime:
    'About five minutes. No teammate invited.',
  privatePathLead:
    'Test it privately on this computer before involving anyone else.',
  reversibleNote: 'You can remove SuperSee from this computer at any time.',
  beforeDownload:
    'A small SuperSee app. Windows may ask for permission — that is expected.',
  smartScreen:
    'Windows shows this for any new app. Choose Run anyway — setup continues normally.',
  waitingConnect:
    'Still connecting — this usually takes under a minute. You don’t need to redo anything.',
  safeScreen:
    'Open a work screen you are comfortable capturing. SuperSee records what is visible when you continue.',
  proofCaptureWaiting:
    'Usually finishes within about 30 seconds. Keep SuperSee open on this computer.',
  proofCaptureMissedTitle: 'Not received yet',
  proofCaptureMissedBody:
    'Connected still counts — this step is only the first picture. We will not send you to an empty dashboard.',
  proofCaptureCheckAgent: 'Confirm SuperSee is still open on this computer.',
  proofCaptureCheckPermission:
    'If it still fails, Windows permissions or antivirus may be blocking capture.',
  proofReadyTitle: 'This screen was captured',
  proofWorking: 'Only this computer. Add one employee when you want the same view of their work.',
  proofFairness:
    'That screenshot proved SuperSee can capture this computer. One screenshot does not judge someone’s productivity.',
  proofProves: [
    'This computer is connected',
    'SuperSee received a real screenshot',
    'You can see when it was taken and which app was open',
  ] as const,
  proofDoesNotProve: [
    'Whether someone is productive',
    'Why they were on that screen',
    'Whether a person is performing well or poorly',
  ] as const,
  /** Bridge: private win → review data / one-employee next proof */
  proofBridgeTitle: 'Capture confirmed',
  proofBridgeFrom: 'This computer',
  proofBridgeTo: 'One employee',
  proofBridgeBody: '',
  afterProofForkLabel: 'Choose your next step',
  afterProofNextProofBadge: 'Next proof',
  afterProofMoreLabel: 'More options',
  afterProofMoreHideLabel: 'Hide options',
  afterProofPrimary: 'Keep reviewing this computer',
  afterProofPrimarySupport:
    'Open screenshots and activity from this computer — still only you.',
  afterProofExploreTitle: 'Try it with one employee',
  afterProofExploreSupport:
    'One real workday is the next meaningful proof. We’ll help you introduce it fairly.',
  afterProofSecondary: 'Try it with one employee',
  afterProofOwnWorkdayTip:
    'Leave SuperSee running today if you want your own full workday to build here.',
  afterProofSampleLink: 'See a full example workday',
  afterProofSampleLabel: 'Example workday — not your company data',
  afterProofSampleCta: 'Try SuperSee with one employee',
  afterProofSampleCtaSupport: 'See the same views using one real workday from your team.',
  afterProofNext:
    'The next meaningful proof is one real employee’s workday—not sample dashboards.',
  proofVisibilityTeach:
    'This ran with the tray icon on, so you could see SuperSee. You can turn that icon off for a computer from the admin panel. It usually shows up in about 10 minutes.',
  /** Soft seed only — no WhatsApp ask; Daily Insights email comes after a real employee day. */
  afterProofDailyHint:
    'After one real employee day, SuperSee can email a short Daily Insights summary so you do not need the dashboard every time.',
  beforeFirstEmployeeKicker: 'Try one',
  beforeFirstEmployeeTitle: 'Introduce SuperSee with one person',
  beforeFirstEmployee:
    'You’ve proved the install. Before anyone else joins, take a moment to set this up fairly.',
  fairSetupGuidanceHeading: 'How to introduce it fairly',
  fairSetupGuidanceLead:
    'Three things to decide before you invite them — so the first day builds trust, not surprise.',
  fairSetupCommitments: [
    {
      title: 'Tell them first',
      detail:
        'Explain why SuperSee is being introduced, what it captures, when it runs, and who can review it.',
    },
    {
      title: 'Keep work and personal separate',
      detail:
        'Prefer a company-managed work device. If they use a personal computer, use a separate work account.',
    },
    {
      title: 'Review patterns, not one moment',
      detail:
        'Use the day as context for a conversation — not a verdict from a single screenshot.',
    },
  ] as const,
  fairSetupNextHeading: 'What happens next',
  fairSetupNextBody:
    'You’ll open the real Add employee form — choose software type, device OS, and whether to email credentials.',
  fairSetupCta: 'Add employee',
  fairSetupCtaSupport: 'Takes about a minute. You can keep reviewing this computer anytime.',
  fairSetupBasicsLabel: 'Privacy and policy basics',
  fairSetupBasicsHideLabel: 'Hide privacy basics',
  fairSetupMoreLabel: 'More options',
  fairSetupMoreHideLabel: 'Hide options',
  fairSetupBasics: [
    {
      title: 'Who can review the data?',
      body: 'Workspace admins can review apps, sites, time, and screenshots.',
    },
    {
      title: 'What should I tell the employee?',
      body: 'Explain the purpose, what is captured, when capture runs, what they will see, and who has access.',
    },
    {
      title: 'What rules apply?',
      body: 'Use your organisation’s monitoring policy and any notice or consent process required where you operate.',
    },
  ] as const,
  /**
   * Objection library — surface only at the decision where resolving it helps the founder move.
   * Prefer action titles over fear headlines.
   */
  objections: {
    afterScreenshot: [
      {
        title: 'One screenshot may look unfair',
        body:
          'Review the day around this moment before drawing conclusions. One frame is context—not a verdict about a person.',
      },
      {
        title: 'Will I need to watch this all day?',
        body:
          'No. Check when something looks off. SuperSee is for attention when it matters—not a day-long spectator sport.',
      },
      {
        title: 'Will this turn me into a micromanager?',
        body:
          'Start with one person and look for patterns across the day. Constant watching creates the micromanager problem; fair review prevents it.',
      },
    ],
    beforeEmployeeFounder: [
      {
        title: 'Introduce SuperSee clearly',
        body:
          'Before installation, tell the employee: why it is being introduced, what will be captured, when capture happens, what they will see, and who can access the information.',
      },
      {
        title: 'Privacy, passwords and client data',
        body:
          'SuperSee does not record typed passwords as keystrokes. The webcam stays off by default. Still ask people to avoid leaving confidential client material on an unlocked work screen when they step away.',
      },
      {
        title: 'Personal computers',
        body:
          'On a personal computer, use a separate work account. SuperSee runs in that account so personal activity stays outside the work window.',
      },
      {
        title: 'If they ask “so you don’t trust us?”',
        body:
          'Answer with purpose, not suspicion: you want a clear picture of the workday so you can help—not catch people out. Starting with one person is how you confirm the setup fairly.',
      },
      {
        title: 'Can a manager misuse it?',
        body:
          'Access stays with workspace admins you control. Set the expectation that screenshots support conversation, not ambush—and that isolated moments are not performance judgements.',
      },
      {
        title: 'What will the employee see and control?',
        body:
          'In Manual mode they can see SuperSee and start and stop it. In Automatic mode they can see it but tracking runs on its own. In Hidden mode there is no day-to-day widget—but that still requires open introduction and policy, not secrecy.',
      },
      {
        title: 'Who installs it?',
        body:
          'Invite by email: they install and sign in. Computer setup: you prepare their work PC (Windows). Mac and Linux employees use invite.',
      },
    ],
    beforeEmployeeAsk: [
      {
        title: 'Will pausing look bad?',
        body:
          'Quiet thinking time is normal work. SuperSee is not a mouse-speed scoreboard—pausing for focus is allowed in Visible mode.',
      },
      {
        title: 'Will one screenshot judge me?',
        body:
          'One screenshot shows one moment. Fair review looks at the whole workday before anyone draws conclusions.',
      },
      {
        title: 'What about personal stuff?',
        body:
          'On a personal computer, use a separate work account. SuperSee runs only there so personal activity stays outside.',
      },
      {
        title: 'Who can see my screen?',
        body:
          'Workspace admins you designate—not every teammate by default. Ask your admin who has access in this workspace.',
      },
    ],
    firstDayReview: [
      {
        title: 'What if the problem is our planning or management?',
        body:
          'Waiting, repeated switching and interrupted work can also point to unclear priorities, slow tools or delayed decisions—not only employee performance.',
      },
      {
        title: 'Will fast employees be punished?',
        body:
          'Speed alone is not the score. Look at outcomes and patterns across the day—not how quickly someone clicks.',
      },
      {
        title: 'Slow software and bad days',
        body:
          'A rough day with tools, networks or blockers shows up in the record. Treat that as context for help, not a personal failing.',
      },
      {
        title: 'Research, tutorials, AI tools and debugging',
        body:
          'Learning and problem-solving often look “busy” in unexpected apps. Read the surrounding timeline before assuming distraction.',
      },
      {
        title: 'Fake busyness',
        body:
          'Constant motion without progress is a pattern across hours—not a single screenshot. Use the day view to tell activity from output.',
      },
      {
        title: 'Job and appraisal fears',
        body:
          'Say out loud how SuperSee is used in reviews: context for conversation, not a secret score. Isolated moments never stand alone.',
      },
    ],
  } as const,
  silentConfirm:
    'Background setup means no day-to-day widget on their screen — it does not mean secret monitoring. Your policy and notice still apply.',
  /**
   * Visible vs Hidden education at the one-employee handoff.
   * Keep founder language: Visible / Hidden — map to invite vs computer setup.
   */
  modeEducationTitle: 'How SuperSee can run for them',
  modeEducationLead:
    'Same work visibility for you either way. What changes is what they see day to day. You can change this later.',
  modeVisible: {
    title: 'Visible',
    subtitle: 'They can see SuperSee',
    body:
      'SuperSee is open on their computer. They know it is running. With the usual invite path they can start and stop it. Best when you want trust and a clear introduction.',
    mapsTo: 'Usually: Invite by email',
    points: [
      'They can see that SuperSee is running',
      'Open introduction — not covert',
      'Good default for a first real employee',
    ],
  },
  modeHidden: {
    title: 'Hidden',
    subtitle: 'Background on a work PC',
    body:
      'No day-to-day widget on their screen. You prepare the Windows work computer. Still introduce SuperSee openly and follow your policy.',
    mapsTo: 'Usually: Prepare computer setup (Windows)',
    points: [
      'Runs in the background without a widget',
      'You install on their work PC',
      'Available for Windows, macOS, and Linux in Add employee',
    ],
  },
  hireCompanion: 'Add someone, then send or download setup for their computer.',
  hireFormComputerTitle: 'Who uses this computer?',
  hireFormInviteTitle: 'Who are you inviting?',
  hireAuthLabel: 'I’ll introduce SuperSee and this PC is covered by our policy',
  hireMoreLabel: 'Need intro words?',
  hireMoreHideLabel: 'Hide intro words',
  modeRecommendBadgeShort: 'Recommended',
  /** @deprecated Prefer modeEducationNoteFor(mode) — static Visible wording. */
  modeEducationNote:
    'You used Visible on this computer. For the first employee, pick the experience that fits them.',
  modeEducationNoteVisible:
    'You used Visible on this computer. For the first employee, pick the experience that fits them.',
  modeEducationNoteHidden:
    'You used Hidden on this computer. For the first employee, pick the experience that fits them.',
  modeRecommendBadge: 'Recommended for first employee',
  modeVisiblePause: {
    title: 'Manual',
    subtitle: 'Invite by email',
    body:
      'SuperSee is open on their computer. They know it is running and start and stop it themselves. Best default for a first real employee.',
    mapsTo: 'Invite by email',
  },
  modeVisibleAdmin: {
    title: 'Automatic',
    subtitle: 'Invite by email',
    body:
      'They can see SuperSee is running, but tracking runs automatically and they can’t start or stop it. Same visibility, tighter control.',
    mapsTo: 'Invite by email',
  },
  whoSeesWhatTitle: 'Who can see what',
  whoSeesWhatBody:
    'Workspace admins review apps, sites, time, and screenshots. The employee sees SuperSee in Manual and Automatic modes; Hidden has no day-to-day widget. Personal time stays separate when tracking is paused or they are off work.',
  dailyInsightsPending:
    'Daily Insights email is not claimed yet. It becomes eligible after a fuller real employee day, shift/day end, and a validated recipient.',
  dailyInsightsReady:
    'Daily Insights email is eligible with your current workflow settings. Open a full day only when something looks off.',
  windowsPrivateTestNote:
    'On this computer · Windows, macOS, or Linux.',
  introScriptVisibleTitle: 'Tell them first — Visible',
  introScriptVisible:
    'We’re adding SuperSee so I can see work on this computer during work hours — apps, sites, and occasional screenshots. You’ll see SuperSee running and can start and stop it. Only workspace admins review that data. Personal time stays separate.',
  introScriptVisibleAdmin:
    'We’re adding SuperSee so I can see work on this computer during work hours — apps, sites, and occasional screenshots. You’ll see SuperSee running; tracking runs automatically, so you can’t start or stop it. Only workspace admins review that data. Personal time stays separate.',
  introScriptHiddenTitle: 'Tell them first — Hidden',
  introScriptHidden:
    'We’re adding SuperSee on this work PC so I can see work during work hours — apps, sites, and occasional screenshots. It runs in the background without a day-to-day widget. It is not secret monitoring: I’m telling you openly. Only workspace admins review that data. Personal time stays separate.',
  introScriptCopyLabel: 'Copy intro',
  inviteWaitTitle: 'Waiting for them to connect',
  inviteWaitLead:
    'Invite is sent. First-day review unlocks after they install, sign in, and SuperSee connects — not before.',
  inviteWaitSteps: [
    'Introduce SuperSee using the script above (or your own words)',
    'They install from the invite email and sign in',
    'Status here shows Connected',
    'Then open first-day review when early signals appear',
  ] as const,
  firstDayWaitingCopy:
    'Invite sent — First day unlocks when they connect and early signals appear. Keep / Scale stay locked until then.',
  firstDayReview:
    'A few minutes on what needs attention. When a full day exists, Daily Insights can summarise it by email—open a full day only when something looks off.',
  privateEvalNote:
    'You can review screenshots, apps, websites and time on this computer. One person for a day or two cannot show team patterns yet.',
  removeDisclosure:
    'Removing SuperSee stops new screenshots from this computer. Screenshots already saved in your SuperSee account are not deleted.',
} as const;

/** Static sample for Ready-step Spot What Repeats preview (inline example — not live PT data). */
export const JOURNEY_TRENDS_PREVIEW = {
  periodLabel: 'Last 14 days · Productivity Trends',
  teamMeta: '1 needs review · 12 logged',
  changes: [
    { tone: 'warn', text: 'Idle stayed high for Sam on 2 of 12 logged days.' },
    { tone: 'ok', text: 'Priya’s active time held steady this period.' },
    { tone: 'info', text: 'Alex shows a normal pattern — no attention flag.' },
  ],
  attention: {
    name: 'Sam Chen',
    initials: 'SC',
    score: '41%',
    idlePct: '58%',
    signal: 'Low Activity Pattern',
    why: 'Idle ran high on 2 logged days — recurring, not a one-off.',
    action: '1-on-1 check-in',
  },
  deepWork: {
    name: 'Sam Chen',
    initials: 'SC',
    sectionLabel: 'Deep Work',
    period: 'Last 14 days · vs previous',
    verdict:
      'Mostly idle when logged (58% of logged time). 18h 40m active across 14 days.',
    daysWorked: '8/14',
    daysWorkedHint: '≥ 15 min active',
    typicalActive: '2.3h',
    productivity: '42%',
    productivityHint: '58% idle while logged',
    tags: ['High idle', 'Deep work 22%'],
    dayStrip: [
      'worked',
      'light',
      'none',
      'worked',
      'worked',
      'light',
      'none',
      'worked',
      'worked',
      'none',
      'light',
      'worked',
      'worked',
      'none',
    ],
    timeByType: [
      { label: 'Browsers & web', pct: 44 },
      { label: 'Coding & tools', pct: 28 },
      { label: 'Communication', pct: 16 },
      { label: 'Other', pct: 12 },
    ],
    topApps: [
      { label: 'Google Chrome', pct: 26 },
      { label: 'Visual Studio Code', pct: 18 },
    ],
  },
} as const;

/**
 * Daily WhatsApp Summary — pitch vs delivery.
 *
 * Contact capture + value pitch is allowed at Ready, after private proof, fair setup,
 * and the trial admin popup (meta.phone_number, no OTP, never hard-block journey CTAs).
 *
 * Actual WhatsApp delivery stays gated until production content, consent, delivery, and opt-out
 * are verified (availableInProduct: false). Delivery opt-in copy must lead with value, never
 * “verify WhatsApp / enable notifications / complete profile”. Always include Maybe later.
 */
export const WHATSAPP_SUMMARY_POLICY = {
  availableInProduct: false,
  trigger:
    'after_founder_views_useful_daily_insights_email_from_real_employee_day',
  /** Moments where live WhatsApp delivery must not be claimed as already on. */
  forbiddenDeliveryMoments: [
    'signup',
    'before_usable_employee_data',
  ] as const,
  /** Contact capture + value pitch is allowed at these activation beats. */
  allowedCaptureMoments: [
    'ready',
    'private_proof',
    'fair_setup',
    'trial_admin_popup',
  ] as const,
  /** @deprecated Prefer forbiddenDeliveryMoments; kept for older readers. */
  forbiddenMoments: [
    'signup',
    'before_usable_employee_data',
  ] as const,
  promptPrimary: 'Send it to WhatsApp',
  promptSecondary: 'Maybe later',
  valueHeadline: 'Get your Daily Insights on WhatsApp',
  valueSupport: 'Get the same short overview without opening the dashboard.',
} as const;

export const ACTIVATION_STATUS_PHASES: Array<{
  id: ActivationStatusPhase;
  label: string;
}> = [
  { id: 'package_prepared', label: 'Package ready' },
  { id: 'installed_locally', label: 'Installed on device' },
  { id: 'auth_heartbeat', label: 'Connected' },
  { id: 'signaling', label: 'Ready to capture' },
  { id: 'first_data', label: 'First data received' },
  { id: 'needs_attention', label: 'Needs attention' },
];

const STORAGE_KEY = 'supersee_activation_journey';
const ANALYTICS_BUFFER_KEY = 'supersee_activation_analytics';

function defaultState(): ActivationJourneyState {
  return {
    schemaVersion: ACTIVATION_SCHEMA_VERSION,
    journeyStatus: 'active',
    evaluationStatus: 'inactive',
    waitingReason: null,
    pausedAtStep: null,
    pausedAt: null,
    resumeDestination: null,
    lastUsefulRoute: null,
    privateTestLifecycle: 'none',
    currentStep: 'goal_selected',
    completedSteps: [],
    goal: null,
    mode: null,
    modeEducationSeen: false,
    privateTestEmployeeId: null,
    privateTestEmail: null,
    privateTestPassword: null,
    installRecoveryBranch: null,
    activationStatusPhase: 'package_prepared',
    firstEmployeeId: null,
    firstEmployeeMode: null,
    actorRoute: null,
    keepPrivateTestData: null,
    privateEvaluationActive: false,
    continuedLater: false,
    finished: false,
    skipped: false,
    emailOtpTracked: false,
    analytics: [],
    exploreVisited: [],
    privateTestStartedAt: null,
    exploreGuideCollapsed: false,
    fairSetupAckSeen: false,
    inviteFirstFromReady: false,
    visibilitySwitchTaught: false,
    employeeVisibilityNudgeShown: false,
    privateTestExitNudgeShown: false,
    connectWaitStartedAt: null,
    purposeOtherText: null,
    pilotTrayOff: false,
    pilotLiveViewAllowed: true,
    pilotScreenshotsOn: true,
    pilotScreenshotIntervalMinutes: null,
    pilotAppsOn: true,
    pilotEmployeeControlled: true,
    pilotShiftLabel: null,
    announcementShared: false,
    announcementSkippedReason: null,
    announcementDraftEdited: false,
    announcementCustomText: null,
    inviteWarnDismissed: false,
    wantsEmployeeInvite: false,
  };
}

/**
 * Collection already chose Hidden or Automatic. Open SuperSee Setup must keep
 * that choice so it downloads Settings / admin MSI. A bare Hidden argument
 * with no collection choice stays Manual.
 */
export function installModeFromCollection(state: {
  mode?: string | null;
  pilotTrayOff?: boolean;
  pilotEmployeeControlled?: boolean;
} | null | undefined): 'hidden' | 'admin_controlled' | 'employee_controlled' {
  const savedMode = String(state?.mode || '').toLowerCase();
  if (
    savedMode === 'hidden' ||
    savedMode === 'silent' ||
    (state?.pilotTrayOff === true && savedMode !== 'admin_controlled')
  ) {
    return 'hidden';
  }
  if (
    savedMode === 'admin_controlled' ||
    savedMode === 'restricted' ||
    (state?.pilotEmployeeControlled === false && state?.pilotTrayOff !== true)
  ) {
    return 'admin_controlled';
  }
  return 'employee_controlled';
}

@Injectable({
  providedIn: 'root',
})
export class ActivationJourneyService {
  private static readonly INTENTIONAL_REENTRY_KEY = 'supersee_activation_intentional_reentry';
  private stateSubject = new BehaviorSubject<ActivationJourneyState>(this.loadState());
  state$ = this.stateSubject.asObservable();
  private apiUrl = config.baseUrl + 'supersee/v1/onboarding/v2/activation';
  private serverSyncTimer: ReturnType<typeof setTimeout> | null = null;
  /** Prevents pagehide + destroy (or Continue later + navigate) from double-firing wait_left. */
  private connectWaitLeftSentForStart: number | null = null;
  /** In-memory mirror of session re-entry so query-param loss cannot bounce to Dashboard. */
  private intentionalReentry: 'menu' | 'restart' | null = null;

  constructor(
    private http: HttpClient,
    private readiness: ActivationReadinessService,
    private tokenEncryptionService: TokenEncryptionService
  ) {
    this.intentionalReentry = this.readIntentionalReentry();
  }

  get isEnabled(): boolean {
    return !!sysConfig.activationJourneyEnabled;
  }

  /** Profile → Journey / Restart: keep allowReentry alive across beat URL sync. */
  beginIntentionalReentry(source: 'menu' | 'restart'): void {
    this.intentionalReentry = source;
    try {
      sessionStorage.setItem(ActivationJourneyService.INTENTIONAL_REENTRY_KEY, source);
    } catch {
      /* ignore */
    }
  }

  clearIntentionalReentry(): void {
    this.intentionalReentry = null;
    try {
      sessionStorage.removeItem(ActivationJourneyService.INTENTIONAL_REENTRY_KEY);
    } catch {
      /* ignore */
    }
  }

  getIntentionalReentry(): 'menu' | 'restart' | null {
    if (this.intentionalReentry === 'menu' || this.intentionalReentry === 'restart') {
      return this.intentionalReentry;
    }
    return this.readIntentionalReentry();
  }

  private readIntentionalReentry(): 'menu' | 'restart' | null {
    try {
      const raw = sessionStorage.getItem(ActivationJourneyService.INTENTIONAL_REENTRY_KEY);
      return raw === 'menu' || raw === 'restart' ? raw : null;
    } catch {
      return null;
    }
  }

  getState(): ActivationJourneyState {
    return this.stateSubject.getValue();
  }

  getStepIndex(stepId?: ActivationStepId): number {
    const id = stepId || this.getState().currentStep;
    return ACTIVATION_STEPS.findIndex((s) => s.id === id);
  }

  /** Map backend step → founder-facing chapter for progress chrome. */
  getChapterId(stepId?: ActivationStepId): JourneyChapterId {
    const state = this.getState();
    const id = stepId || state.currentStep;
    switch (id) {
      case 'goal_selected':
      case 'plan_viewed':
        return 'purpose';
      case 'collection':
        return 'collection';
      case 'setup_confirmed':
      case 'announce':
      case 'mode_education':
      case 'private_test':
      case 'private_proof':
      case 'first_employee':
        return 'invite';
      case 'first_day_review':
        return 'review';
      default:
        return 'purpose';
    }
  }

  isJourneyB(stepId?: ActivationStepId): boolean {
    const chapter = this.getChapterId(stepId);
    return chapter === 'invite' || chapter === 'review' || chapter === 'announce';
  }

  getVisibleChapters(_stepId?: ActivationStepId) {
    return JOURNEY_CHAPTERS_ALL;
  }

  getVisibleChapterIndex(stepId?: ActivationStepId): number {
    const chapters = this.getVisibleChapters(stepId);
    const chapterId = this.getChapterId(stepId);
    const idx = chapters.findIndex((c) => c.id === chapterId);
    return idx >= 0 ? idx : 0;
  }

  getTotalSteps(): number {
    return this.getVisibleChapters().length;
  }

  /** Map setup method onto stable ActorRoute values. */
  actorRouteForSetupMethod(method: SetupMethod): ActorRoute {
    return method === 'invite' ? 'employee_runs' : 'hero_installs';
  }

  setupMethodFromActorRoute(route: ActorRoute | null): SetupMethod | null {
    if (!route) {
      return null;
    }
    return route === 'employee_runs' ? 'invite' : 'computer_setup';
  }

  getGoalMeta(goalId: ActivationGoalId | null) {
    return ACTIVATION_GOALS.find((g) => g.id === goalId) || null;
  }

  /** StoryBrand reflection line for the currently selected goal. */
  getGoalReflection(goalId?: ActivationGoalId | null): string {
    const id = goalId !== undefined ? goalId : this.getState().goal;
    return this.getGoalMeta(id)?.reflection || '';
  }

  /** Visible vs silent framing for private-test copy (product modes map underneath). */
  isSilentMode(mode?: string | null): boolean {
    const m = mode ?? this.getState().mode;
    return m === 'hidden' || m === 'silent';
  }

  privateTestModeLabel(mode?: string | null): string {
    if (this.isSilentMode(mode)) {
      return 'Background — no day-to-day widget';
    }
    if ((mode ?? this.getState().mode) === 'admin_controlled') {
      return 'Automatic — tracking runs on its own';
    }
    return 'Manual — you start and stop it';
  }

  /** Load journey from WordPress DB (usermeta + onboarding progress). */
  hydrateFromServer(): Observable<ActivationJourneyState> {
    if (!this.isEnabled) {
      return of(this.getState());
    }
    return this.http.get<{ success?: boolean; state?: ActivationJourneyState }>(this.apiUrl).pipe(
      map((res) => {
        const server = migrateActivationJourneyState(res?.state || null);
        // Read local state when the response arrives, not when the GET started,
        // so a Continue during the flight is not overwritten.
        const localNow = this.getState();
        const merged = mergePreferringFurtherAlong(localNow, server);
        // Menu/restart: the click owns the step. List order is not walk order,
        // so a "further" server step must not rewind Install back to Preview.
        if (this.getIntentionalReentry()) {
          merged.currentStep = localNow.currentStep;
          merged.completedSteps = localNow.completedSteps;
          merged.journeyStatus = localNow.journeyStatus;
          merged.finished = localNow.finished;
        }
        this.persistLocal(merged, false);
        return merged;
      }),
      catchError((err) => {
        console.warn('[ActivationJourney] hydrateFromServer failed; using local cache', err);
        return of(this.getState());
      })
    );
  }

  /** Flush pending journey POST so navigation after completePrivateProof cannot be overwritten. */
  flushServerSync(): Observable<unknown> {
    if (this.serverSyncTimer) {
      clearTimeout(this.serverSyncTimer);
      this.serverSyncTimer = null;
    }
    if (!this.isEnabled) {
      return of(null);
    }
    const state = migrateActivationJourneyState({ ...this.getState(), privateTestPassword: null });
    return this.http.post(this.apiUrl, { state }).pipe(
      catchError((err) => {
        console.warn('[ActivationJourney] flushServerSync failed', err);
        return of(null);
      })
    );
  }

  /** Reset journey + classic onboarding progress in DB, then local cache. */
  resetFromServer(): Observable<ActivationJourneyState> {
    return this.http.post<{ success?: boolean; state?: ActivationJourneyState }>(`${this.apiUrl}/reset`, {}).pipe(
      map((res) => {
        const fresh = migrateActivationJourneyState(res?.state || null);
        try {
          localStorage.removeItem('supersee_onboarding_completed');
          localStorage.removeItem('supersee_onboarding_checklist');
          localStorage.removeItem('supersee_onboarding_employee');
          localStorage.removeItem(ANALYTICS_BUFFER_KEY);
        } catch {
          /* ignore */
        }
        this.persistLocal(fresh, false);
        return fresh;
      }),
      tap(() => console.info('[ActivationJourney] Reset in DB — start from Goal on /setup'))
    );
  }

  private loadState(): ActivationJourneyState {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        return migrateActivationJourneyState(JSON.parse(raw));
      }
    } catch (error) {
      console.error('[ActivationJourney] Failed to load state', error);
    }
    return defaultState();
  }

  private persistLocal(state: ActivationJourneyState, syncServer: boolean): void {
    const scrubbed = migrateActivationJourneyState({ ...state, privateTestPassword: null });
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(scrubbed));
      this.stateSubject.next(scrubbed);
    } catch (error) {
      console.error('[ActivationJourney] Failed to save state', error);
      this.stateSubject.next(scrubbed);
    }
    if (syncServer) {
      this.scheduleServerSync(scrubbed);
    }
  }

  private persist(state: ActivationJourneyState): void {
    this.persistLocal(state, true);
  }

  private scheduleServerSync(state: ActivationJourneyState): void {
    if (!this.isEnabled) {
      return;
    }
    if (this.serverSyncTimer) {
      clearTimeout(this.serverSyncTimer);
    }
    this.serverSyncTimer = setTimeout(() => {
      const payload = migrateActivationJourneyState({ ...state, privateTestPassword: null });
      this.http.post(this.apiUrl, { state: payload }).pipe(
        catchError((err) => {
          console.warn('[ActivationJourney] server sync failed', err);
          return of(null);
        })
      ).subscribe();
    }, 400);
  }

  private patch(partial: Partial<ActivationJourneyState>): ActivationJourneyState {
    const next =migrateActivationJourneyState({ ...this.getState(), ...partial });
    this.persist(next);
    return next;
  }

  trackEvent(name: string, payload?: Record<string, unknown>): void {
    // Sample / example events never count as real-proof funnel milestones.
    const sampleEvent =
      name.startsWith('example_workday_') ||
      name.startsWith('example_preview_') ||
      name === 'proof_sample_workday_opened' ||
      name === 'ready_preview_selected' ||
      name === 'control_awareness_shown';
    const enriched = {
      ...(payload || {}),
      ...(sampleEvent && payload?.['data_source'] == null
        ? { data_source: name === 'ready_preview_selected' ? 'inline_example' : 'scoped_example' }
        : {}),
    };
    const event: ActivationAnalyticsEvent = {
      name,
      payload: Object.keys(enriched).length ? enriched : undefined,
      at: Date.now(),
    };
    const state = this.getState();
    const analytics = [...(state.analytics || []), event].slice(-200);
    this.patch({ analytics });

    try {
      const bufRaw = localStorage.getItem(ANALYTICS_BUFFER_KEY);
      const buf: ActivationAnalyticsEvent[] = bufRaw ? JSON.parse(bufRaw) : [];
      buf.push(event);
      localStorage.setItem(ANALYTICS_BUFFER_KEY, JSON.stringify(buf.slice(-500)));
    } catch {
      /* local buffer is best-effort */
    }

    if (typeof console !== 'undefined') {
      console.info('[ActivationAnalytics]', name, enriched || {});
    }

    try {
      // Clarity (UX) + GTM dataLayer (GA4/ads) from one taxonomy.
      trackProductEvent(name, enriched);
    } catch {
      /* Product analytics sinks are optional */
    }
  }

  /** Fire once when session suggests the user just finished email OTP. */
  maybeTrackEmailOtpVerified(): void {
    const state = this.getState();
    if (state.emailOtpTracked) {
      return;
    }
    const otpHint =
      sessionStorage.getItem('supersee_email_otp_verified') === '1' ||
      sessionStorage.getItem('email_otp_verified') === '1' ||
      localStorage.getItem('supersee_just_verified_otp') === '1';
    if (otpHint) {
      this.trackEvent('email_otp_verified');
      this.patch({ emailOtpTracked: true });
      sessionStorage.removeItem('supersee_email_otp_verified');
      sessionStorage.removeItem('email_otp_verified');
      localStorage.removeItem('supersee_just_verified_otp');
    }
  }

  setGoal(goal: ActivationGoalId): void {
    this.patch({ goal });
    this.trackEvent('goal_selected', { goal });
  }

  /** Ready Continue: identity only → Purpose (`plan_viewed`). */
  advanceReadyToUnderstand(): void {
    this.completeStep('goal_selected');
    this.goToStep('plan_viewed');
  }

  /**
   * Optional private test from Invite — does not skip Purpose/Collection/Access/Announce.
   * Does not create privateTestEmployeeId — that happens on Download.
   */
  startPrivateTestFromReady(_mode?: 'employee_controlled' | 'hidden' | 'admin_controlled'): void {
    const mode = installModeFromCollection(this.getState());
    this.patch({
      mode,
      modeEducationSeen: true,
      privateEvaluationActive: false,
      connectWaitStartedAt: null,
    });
    this.trackEvent('private_test_mode_selected', {
      mode,
      source: 'invite_optional',
    });
    this.completeStep('mode_education');
    this.goToStep('private_test');
  }

  /** Purpose chip selected on Purpose screen. */
  setTrustFirstPurpose(purposeId: TrustFirstPurposeId, otherText?: string | null): void {
    const goal = purposeId as ActivationGoalId;
    // Picking a card and then pressing Continue both call this; only a real change is an event.
    const state = this.getState();
    const changed = state.goal !== goal;
    // Skip noop patch on Continue — a redundant state$ emission with currentStep still
    // on plan_viewed was snapping Collection Continuues back to Purpose mid-click.
    if (!changed && (state.purposeOtherText == null || state.purposeOtherText === '')) {
      return;
    }
    this.patch({
      goal,
      purposeOtherText: null,
    });
    if (changed) {
      this.trackEvent('onboarding_purpose_set', { purpose: purposeId });
      this.trackEvent('goal_selected', { goal, source: 'purpose' });
    }
  }

  completePurposeAndAdvance(): void {
    const state = this.getState();
    if (!state.goal) {
      return;
    }
    if (state.goal === 'other' && !(state.purposeOtherText || '').trim()) {
      return;
    }
    // One patch only. completeStep + trackEvent + goToStep used to emit 3× state$
    // while still on plan_viewed, so URL sync raced and snapped Collection Continuues
    // back to Purpose on completed-journey menu re-entry.
    const completedSteps: ActivationStepId[] = state.completedSteps.includes('plan_viewed')
      ? state.completedSteps
      : [...state.completedSteps, 'plan_viewed'];
    this.patch({
      completedSteps,
      currentStep: 'collection',
      continuedLater: false,
      journeyStatus:
        state.journeyStatus === 'completed' || state.journeyStatus === 'skipped'
          ? state.journeyStatus
          : 'active',
      waitingReason: null,
      pausedAtStep: null,
      pausedAt: null,
      resumeDestination: null,
    });
    this.trackEvent('plan_viewed', { source: 'purpose' });
  }

  savePilotCollection(snapshot: TrustFirstCollectionSnapshot): void {
    // Hidden keeps employeeControlled true and turns the tray off. Tray wins, or
    // that combination is stored as Manual and the install screen skips Settings.
    const mode = !snapshot.trayOn
      ? 'hidden'
      : snapshot.employeeControlled
        ? 'employee_controlled'
        : 'admin_controlled';
    this.patch({
      pilotAppsOn: snapshot.appsOn,
      pilotScreenshotsOn: snapshot.screenshotsOn,
      pilotScreenshotIntervalMinutes: snapshot.screenshotIntervalMinutes,
      pilotLiveViewAllowed: snapshot.liveViewAllowed,
      pilotEmployeeControlled: snapshot.employeeControlled,
      pilotTrayOff: !snapshot.trayOn,
      pilotShiftLabel: snapshot.shiftLabel,
      mode,
      announcementDraftEdited: false,
      announcementCustomText: null,
    });
  }

  completeCollectionAndAdvance(): void {
    const state = this.getState();
    const collectingPause =
      state.waitingReason === 'employee_data' &&
      (state.completedSteps || []).includes('first_employee') &&
      !!state.firstEmployeeId;
    this.completeStep('collection');
    this.trackEvent('onboarding_collection_confirmed');
    // Preview fork (download to test | invite by email). Clear leftover hire/private-test
    // wait state so Save never dumps straight into “waiting for connection”.
    this.patch({
      wantsEmployeeInvite: false,
      privateEvaluationActive: false,
      evaluationStatus: 'inactive',
      connectWaitStartedAt: null,
      waitingReason: collectingPause ? state.waitingReason : null,
      ...(collectingPause
        ? {}
        : {
            firstEmployeeId: null,
            actorRoute: null,
            firstEmployeeMode: null,
          }),
    });
    this.goToStep('first_employee');
  }

  /** Founder chose to invite someone — the team note comes first, then the invite. */
  beginEmployeeInvite(): void {
    this.patch({ wantsEmployeeInvite: true });
    this.goToStep('announce');
  }

  completeAccessAndAdvance(): void {
    this.completeStep('setup_confirmed');
    this.trackEvent('onboarding_access_set');
    this.goToStep('announce');
  }

  getCollectionSnapshotFromState(): TrustFirstCollectionSnapshot {
    const state = this.getState();
    const saved = (state.completedSteps || []).includes('collection');
    const screenshotsOn = saved ? !!state.pilotScreenshotsOn : true;
    const liveViewAllowed = saved ? !!state.pilotLiveViewAllowed : true;
    return {
      appsOn: state.pilotAppsOn !== false,
      hoursIncluded: true,
      screenshotsOn,
      screenshotIntervalMinutes: state.pilotScreenshotIntervalMinutes,
      liveViewAllowed,
      liveViewAuditNote: liveViewAllowed,
      employeeControlled: state.pilotEmployeeControlled !== false,
      trayOn: !state.pilotTrayOff,
      shiftLabel: state.pilotShiftLabel,
    };
  }

  buildAnnouncementContext(viewerLabel: string = 'Workspace admins') {
    const state = this.getState();
    return {
      purposeId: (state.goal as TrustFirstPurposeId) || null,
      purposeOtherText: state.purposeOtherText,
      collection: this.getCollectionSnapshotFromState(),
      viewerLabel,
    };
  }

  getGeneratedAnnouncement(viewerLabel?: string): string {
    return buildTrustFirstAnnouncement(this.buildAnnouncementContext(viewerLabel));
  }

  getAnnouncementFacts(viewerLabel?: string) {
    return buildTrustFirstAnnounceFacts(this.buildAnnouncementContext(viewerLabel));
  }

  getAnnouncementText(viewerLabel?: string): string {
    const state = this.getState();
    if (state.announcementDraftEdited && state.announcementCustomText != null) {
      return state.announcementCustomText;
    }
    return this.getGeneratedAnnouncement(viewerLabel);
  }

  setAnnouncementCustomText(text: string): void {
    this.patch({
      announcementDraftEdited: true,
      announcementCustomText: text,
    });
  }

  resetAnnouncementToSettings(): void {
    this.patch({
      announcementDraftEdited: false,
      announcementCustomText: null,
    });
  }

  getAnnouncementFaq(viewerLabel?: string) {
    return buildTrustFirstFaq(this.buildAnnouncementContext(viewerLabel));
  }

  getAnnouncementFaqCopy(viewerLabel?: string): string {
    return formatFaqForCopy(this.getAnnouncementFaq(viewerLabel));
  }

  getAnnouncementAckTemplate(viewerLabel?: string): string {
    return buildTrustFirstAckTemplate(this.buildAnnouncementContext(viewerLabel));
  }

  getPurposeClause(): string {
    const state = this.getState();
    return purposeAnnouncementClause(state.goal as TrustFirstPurposeId, state.purposeOtherText);
  }

  markAnnouncementCopied(action: 'announcement' | 'faq' | 'email' | 'ack'): void {
    this.trackEvent('onboarding_announce_copied', { action });
  }

  completeAnnounceShared(): void {
    this.patch({
      announcementShared: true,
      announcementSkippedReason: null,
    });
    this.completeStep('announce');
    this.trackEvent('onboarding_announce_copied', { action: 'shared' });
    this.goToStep('first_employee');
  }

  completeAnnounceSkipped(): void {
    this.patch({
      announcementShared: false,
      announcementSkippedReason: 'skipped',
    });
    this.completeStep('announce');
    this.trackEvent('onboarding_announce_copied', { action: 'skipped' });
    this.goToStep('first_employee');
  }

  dismissInviteWarn(): void {
    this.patch({ inviteWarnDismissed: true });
  }

  needsInviteAnnouncementWarn(): boolean {
    const state = this.getState();
    if (state.inviteWarnDismissed || shouldSkipAnnounce(state)) {
      return false;
    }
    return true;
  }

  markInviteSent(): void {
    this.trackEvent('onboarding_invite_sent');
  }

  markFirstReviewOpened(): void {
    this.trackEvent('onboarding_first_review_opened');
  }

  markVisibilitySwitchTaught(): void {
    if (this.getState().visibilitySwitchTaught) {
      return;
    }
    this.patch({ visibilitySwitchTaught: true });
    this.trackEvent('visibility_switch_taught');
  }

  markEmployeeVisibilityNudgeShown(): void {
    if (this.getState().employeeVisibilityNudgeShown) {
      return;
    }
    this.patch({ employeeVisibilityNudgeShown: true });
    this.trackEvent('employee_visibility_nudge_shown');
  }

  /** Once per journey. Continue later before this computer connects. */
  markPrivateTestExitNudgeShown(): void {
    if (this.getState().privateTestExitNudgeShown) {
      return;
    }
    this.patch({ privateTestExitNudgeShown: true });
    this.trackEvent('private_test_exit_nudge_shown');
  }

  /** First-employee mode screen note from the private-test mode that was actually used. */
  modeEducationNoteFor(mode?: string | null): string {
    if (this.isSilentMode(mode)) {
      return JOURNEY_GUIDE_COPY.modeEducationNoteHidden;
    }
    return JOURNEY_GUIDE_COPY.modeEducationNoteVisible;
  }

  /**
   * @deprecated Ready no longer shortcuts to Invite — phone and desktop both Continue
   * through Purpose/Collection to See it. Kept for resume of older invite-first sessions
   * and unit coverage of inviteFirstFromReady destination handling.
   */
  startInviteFirstFromReady(): void {
    const done = new Set(this.getState().completedSteps || []);
    if (!done.has('collection')) {
      this.savePilotCollection(this.getCollectionSnapshotFromState());
    }
    this.patch({
      inviteFirstFromReady: true,
      wantsEmployeeInvite: true,
      actorRoute: 'employee_runs',
      privateEvaluationActive: false,
    });
    this.completeStep('goal_selected');
    if (!done.has('plan_viewed')) {
      this.completeStep('plan_viewed');
    }
    if (!done.has('collection')) {
      this.completeStep('collection');
    }
    this.goToStep('first_employee');
  }

  /** Phone/tablet cannot install the desktop agent — leave install nest for Invite. */
  divertMobileFromPrivateTestInstall(): void {
    this.patch({
      wantsEmployeeInvite: true,
      privateEvaluationActive: false,
    });
    this.goToStep('first_employee');
  }

  /** Cancel Add-employee handoff / leave invite-first trap without wiping Invite choice. */
  clearInviteFirstAutoOpen(): void {
    if (!this.getState().inviteFirstFromReady) {
      return;
    }
    this.patch({ inviteFirstFromReady: false });
  }

  /** @deprecated Prefer startPrivateTestFromReady — kept for mid-journey resume compatibility. */
  completeGoalAndAdvance(): void {
    if (!this.getState().goal) {
      this.patch({ goal: 'understand_team_work' });
    }
    this.completePurposeAndAdvance();
  }

  markPlanViewed(): void {
    this.completePurposeAndAdvance();
  }

  setMode(mode: string): void {
    this.patch({ mode, modeEducationSeen: true });
    this.trackEvent('private_test_mode_selected', { mode });
  }

  /** Adjust / first-employee mode — does not overwrite private-test `mode`. */
  setFirstEmployeeMode(mode: string): void {
    this.patch({ firstEmployeeMode: mode, modeEducationSeen: true });
    this.trackEvent('first_employee_mode_selected', { mode });
  }

  completeModeEducation(): void {
    if (!this.getState().mode) {
      this.patch({ mode: 'employee_controlled', modeEducationSeen: true });
    }
    this.completeStep('mode_education');
    this.goToStep('private_test');
  }

  startPrivateEvaluation(): void {
    this.trackEvent('private_evaluation_started', {
      employeeId: this.getState().privateTestEmployeeId,
    });
    this.completeStep('private_proof');
    const state = this.getState();
    this.patch({
      privateEvaluationActive: true,
      evaluationStatus: 'private_active',
      journeyStatus: 'active',
      continuedLater: false,
      waitingReason: null,
      pausedAtStep: null,
      pausedAt: null,
      resumeDestination: 'explore',
      currentStep: 'private_proof',
      privateTestStartedAt: state.privateTestStartedAt || new Date().toISOString(),
      exploreGuideCollapsed: false,
      privateTestLifecycle: state.privateTestEmployeeId ? 'active' : state.privateTestLifecycle,
    });
  }

  resumeFromPrivateEvaluation(): void {
    this.patch({
      privateEvaluationActive: false,
      evaluationStatus: 'inactive',
      continuedLater: false,
      journeyStatus: 'active',
      waitingReason: null,
      pausedAtStep: null,
      pausedAt: null,
    });
    this.completePrivateProof();
  }

  /** Persist that founder opened post-proof fairness content (proves / concerns). */
  markFairSetupAckSeen(source: 'proof_bounds' | 'proof_concerns'): void {
    if (this.getState().fairSetupAckSeen) {
      return;
    }
    this.patch({ fairSetupAckSeen: true });
    this.trackEvent('fair_setup_ack_seen', { source });
  }

  shouldSkipFairSetup(): boolean {
    return shouldSkipFairSetup(this.getState());
  }

  savePrivateTestIdentity(opts: {
    employeeId: number;
    email?: string | null;
    password?: string | null;
  }): void {
    // Never persist plaintext password in localStorage journey blob (keep password in memory only).
    void opts.password;
    const state = this.getState();
    this.patch({
      privateTestEmployeeId: opts.employeeId,
      privateTestEmail: opts.email || null,
      privateTestPassword: null,
      activationStatusPhase: 'package_prepared',
      privateTestStartedAt: state.privateTestStartedAt || new Date().toISOString(),
      privateTestLifecycle:
        state.privateTestLifecycle === 'none' || state.privateTestLifecycle === 'archived'
          ? 'active'
          : state.privateTestLifecycle,
    });
    this.trackEvent('private_test_identity_saved', { employeeId: opts.employeeId });
  }

  /**
   * Drop a private-test employee id that no longer exists (e.g. deleted from the dashboard)
   * so setup can provision a new one instead of retrying credentials on a dead user.
   */
  clearPrivateTestIdentity(reason: string = 'employee_missing'): void {
    this.patch({
      privateTestEmployeeId: null,
      privateTestEmail: null,
      privateTestPassword: null,
      privateTestLifecycle: 'archived',
      activationStatusPhase: 'package_prepared',
      connectWaitStartedAt: null,
    });
    this.trackEvent('private_test_identity_cleared', { reason });
  }

  markExploreVisited(stopId: ExploreStopId): void {
    const state = this.getState();
    const visited = new Set(state.exploreVisited || []);
    if (visited.has(stopId)) {
      return;
    }
    visited.add(stopId);
    this.patch({ exploreVisited: Array.from(visited) });
    this.trackEvent('explore_stop_visited', { stop: stopId });
    if (stopId === 'own_workday' || stopId === 'work_replay') {
      this.trackEvent('own_workday_reviewed');
    }
  }

  setExploreGuideCollapsed(collapsed: boolean): void {
    this.patch({ exploreGuideCollapsed: collapsed });
  }

  /**
   * Founder private-test day readiness.
   * Prefer evidence facade when callers have setEvidence; otherwise transitional wall-clock fallback
   * until install/explore packages wire Firebase/screenshot/log snapshots.
   */
  isOwnWorkdayReady(): boolean {
    const evaluated = this.readiness.evaluate();
    if (evaluated.hasEvidenceInput) {
      return evaluated.founderDayReady;
    }
    return this.legacyOwnWorkdayWallClockReady();
  }

  /** @deprecated Prefer ActivationReadinessService evidence; kept as transitional fallback. */
  private legacyOwnWorkdayWallClockReady(): boolean {
    const startedAt = this.getState().privateTestStartedAt;
    if (!startedAt) {
      return false;
    }
    const started = Date.parse(startedAt);
    if (!Number.isFinite(started)) {
      return false;
    }
    const now = Date.now();
    const fourHoursMs = 4 * 60 * 60 * 1000;
    if (now - started >= fourHoursMs) {
      return true;
    }
    const startDay = new Date(started).toDateString();
    const today = new Date().toDateString();
    return startDay !== today;
  }

  maybeTrackOwnWorkdayReady(): void {
    if (!this.isOwnWorkdayReady()) {
      return;
    }
    const state = this.getState();
    if (state.analytics?.some((e) => e.name === 'own_workday_ready')) {
      return;
    }
    this.trackEvent('own_workday_ready', {
      employeeId: state.privateTestEmployeeId,
    });
  }

  /** Map a URL path to an explore stop (sidebar or guide navigation). */
  exploreStopFromUrl(url: string): ExploreStopId | null {
    const path = (url || '').split('?')[0];
    if (path.startsWith('/screenshots') || path.startsWith('/user-details')) {
      return 'screenshots';
    }
    if (path.startsWith('/reports') || path === '/reports') {
      return 'apps';
    }
    if (path.startsWith('/user-report')) {
      return 'hours';
    }
    if (path.startsWith('/work-replay')) {
      return 'work_replay';
    }
    if (path.includes('/setup/example-workday') || path.includes('example-workday')) {
      return 'example_workday';
    }
    return null;
  }

  /**
   * Work Replay guide unlock: founder-day evidence ready (not wall-clock alone when evidence exists).
   * Entitlement date checks are applied by the floating guide before navigation.
   */
  isWorkReplayGuideDataReady(): boolean {
    return this.isOwnWorkdayReady();
  }

  /** Private-test calendar date (YYYY-MM-DD) for Work Replay deep links, or null. */
  privateTestReplayDateYmd(): string | null {
    const startedAt = this.getState().privateTestStartedAt;
    if (!startedAt) {
      return null;
    }
    const d = new Date(startedAt);
    if (!Number.isFinite(d.getTime())) {
      return null;
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  setInstallRecoveryBranch(branch: InstallRecoveryBranch): void {
    this.patch({ installRecoveryBranch: branch });
    if (branch === 'continue_later') {
      this.continueLater();
      return;
    }
    if (branch === 'pending') {
      this.setStatusPhase('installed_locally');
    }
  }

  setStatusPhase(phase: ActivationStatusPhase): void {
    this.patch({ activationStatusPhase: phase });
    if (phase === 'signaling' || phase === 'first_data') {
      this.trackEvent('device_connected', { phase });
    }
  }

  /**
   * Start (or reset) the Download → online wait. Call only after the installer file is requested.
   */
  beginConnectWait(opts?: {
    employeeId?: number | string | null;
    os?: string | null;
    reset?: boolean;
  }): number {
    const existing = this.getState().connectWaitStartedAt;
    const startedAt = opts?.reset || !existing ? Date.now() : existing;
    if (!existing || opts?.reset) {
      this.connectWaitLeftSentForStart = null;
    }
    this.patch({ connectWaitStartedAt: startedAt });
    if (!existing || opts?.reset) {
      this.trackEvent('connect_wait_started', {
        employeeId: opts?.employeeId ?? this.getState().privateTestEmployeeId,
        os: opts?.os || null,
      });
    }
    return startedAt;
  }

  clearConnectWait(): void {
    if (this.getState().connectWaitStartedAt == null) {
      return;
    }
    this.patch({ connectWaitStartedAt: null });
  }

  markDeviceConnectedFromWatch(opts: {
    how: 'watched' | 'already_online';
    employeeId?: number | string | null;
    step?: string | null;
  }): void {
    const started = this.getState().connectWaitStartedAt;
    const waitMs = started ? Math.max(0, Date.now() - started) : 0;
    this.trackEvent('device_connected', {
      wait_ms: waitMs,
      how: opts.how,
      employeeId: opts.employeeId ?? this.getState().privateTestEmployeeId,
      step: opts.step || this.getState().currentStep,
    });
    this.connectWaitLeftSentForStart = null;
    this.patch({ connectWaitStartedAt: null });
  }

  markConnectWaitLeft(reason: 'continue_later' | 'navigate' | 'pagehide' | 'destroy'): boolean {
    const started = this.getState().connectWaitStartedAt;
    if (!started) {
      return false;
    }
    if (this.connectWaitLeftSentForStart === started) {
      return false;
    }
    const phase = this.getState().activationStatusPhase;
    if (phase === 'auth_heartbeat' || phase === 'signaling' || phase === 'first_data') {
      return false;
    }
    this.trackEvent('connect_wait_left', {
      wait_ms: Math.max(0, Date.now() - started),
      reason,
      step: this.getState().currentStep,
    });
    this.connectWaitLeftSentForStart = started;
    return true;
  }

  completePrivateTest(): void {
    this.completeStep('private_test');
    this.goToStep('private_proof');
  }

  /** Mark screenshot viewed; stay on proof until the hero continues to setup confirm. */
  markPrivateProofViewed(payload?: Record<string, unknown>): void {
    this.trackEvent('private_screenshot_viewed', payload);
    this.setStatusPhase('first_data');
  }

  /** Optional private test returns to Invite after proof. */
  completePrivateProof(): void {
    this.completeStep('private_proof');
    this.trackEvent('private_proof_to_invite', { reason: 'optional_private_test' });
    this.goToStep('first_employee');
  }

  /**
   * Private proof → invite the pilot (same path as “Invite an employee”).
   * Team note first when it is still incomplete; otherwise the invite form.
   */
  completePrivateProofThenInvite(): void {
    this.completeStep('private_proof');
    this.trackEvent('private_proof_to_invite', { reason: 'add_one_employee' });
    this.beginEmployeeInvite();
  }

  /**
   * Example workday CTA → fair-setup confirm (or first employee when fair-setup is skippable).
   * Only soft-completes private proof if it was already done; otherwise caller should send founder to screenshot.
   */
  advanceFromExampleWorkdayToConfirm(): boolean {
    const state = this.getState();
    const proofDone = (state.completedSteps || []).includes('private_proof');
    if (!proofDone) {
      this.trackEvent('example_workday_blocked_without_proof');
      return false;
    }
    const required: ActivationStepId[] = [
      'goal_selected',
      'plan_viewed',
      'mode_education',
      'private_test',
      'private_proof',
    ];
    const completed = new Set<ActivationStepId>(state.completedSteps || []);
    required.forEach((id) => completed.add(id));
    this.patch({
      goal: state.goal || 'understand_team_work',
      mode: state.mode || 'employee_controlled',
      modeEducationSeen: true,
      completedSteps: Array.from(completed),
      continuedLater: false,
      privateEvaluationActive: false,
      evaluationStatus: 'inactive',
    });
    this.trackEvent('fair_setup_skipped', { reason: 'example_workday', ack: this.getState().fairSetupAckSeen });
    this.completeStep('setup_confirmed');
    this.goToStep('first_employee');
    return true;
  }

  completeSetupConfirmed(): void {
    this.completeAccessAndAdvance();
  }

  /** Authoritative server mutation response — do not merge with local cache. */
  replaceWithServerState(state: ActivationJourneyState): ActivationJourneyState {
    const next = migrateActivationJourneyState(state);
    this.persistLocal(next, false);
    return next;
  }

  setKeepPrivateTestData(keep: boolean | null): void {
    this.patch({ keepPrivateTestData: keep });
  }

  setPrivateTestLifecycle(lifecycle: PrivateTestLifecycle): void {
    this.patch({ privateTestLifecycle: lifecycle });
  }

  setFirstEmployeeMeta(opts: {
    employeeId?: number | null;
    mode?: string | null;
    actorRoute?: ActorRoute | null;
  }): void {
    this.patch({
      firstEmployeeId: opts.employeeId ?? this.getState().firstEmployeeId,
      firstEmployeeMode: opts.mode ?? this.getState().firstEmployeeMode,
      actorRoute: opts.actorRoute ?? this.getState().actorRoute,
    });
  }

  /**
   * Finish Try one after heartbeat.
   * reviewReady=false → collecting pause (stay on first_employee; Dashboard via Done for now).
   * reviewReady=true → open Review. Never emits first_day_review_viewed (route render owns that).
   */
  completeFirstEmployee(opts: { reviewReady: boolean } = { reviewReady: true }): void {
    const state = this.getState();
    const alreadyCompleted = (state.completedSteps || []).includes('first_employee');
    if (!alreadyCompleted) {
      this.trackEvent('first_employee_added', {
        employeeId: state.firstEmployeeId,
        actorRoute: state.actorRoute,
      });
      this.completeStep('first_employee');
    }

    if (!opts.reviewReady) {
      this.patch({
        currentStep: 'first_employee',
        continuedLater: true,
        journeyStatus: 'paused',
        waitingReason: 'employee_data',
        pausedAtStep: 'first_employee',
        pausedAt: new Date().toISOString(),
        resumeDestination: 'first_employee',
        privateEvaluationActive: false,
        evaluationStatus:
          state.evaluationStatus === 'private_active' ? 'inactive' : state.evaluationStatus,
      });
      return;
    }

    this.patch({
      currentStep: 'first_day_review',
      continuedLater: false,
      journeyStatus: 'active',
      waitingReason: null,
      pausedAtStep: null,
      pausedAt: null,
      resumeDestination: null,
      privateEvaluationActive: false,
      evaluationStatus:
        state.evaluationStatus === 'private_active' ? 'inactive' : state.evaluationStatus,
    });
  }

  /** First-day review unlocks only from the strict readiness facade. */
  canEnterFirstDayReview(_opts?: {
    firstEmployeeId?: number | null;
    agentOnline?: boolean;
    hasScreenshotOrSignals?: boolean;
  }): boolean {
    return this.readiness.isEarlyReviewReady();
  }

  markFirstDayReviewViewed(): void {
    const state = this.getState();
    if (state.analytics?.some((e) => e.name === 'first_day_review_viewed')) {
      return;
    }
    this.trackEvent('first_day_review_viewed');
  }

  markFirstDayReviewActionable(): void {
    const state = this.getState();
    if (state.analytics?.some((e) => e.name === 'first_day_review_actionable')) {
      return;
    }
    this.trackEvent('first_day_review_actionable');
  }

  finishJourney(source: 'keep' | 'adjust' | 'scale' | 'dashboard' = 'dashboard'): void {
    this.trackEvent('activation_journey_finished', { source });
    this.completeStep('first_day_review');
    this.patch({
      finished: true,
      continuedLater: false,
      journeyStatus: 'completed',
      evaluationStatus: 'complete',
      waitingReason: null,
      pausedAtStep: null,
      pausedAt: null,
      resumeDestination: null,
      privateEvaluationActive: false,
    });
  }

  /** Paid workspaces (`quota_status === active`) — not trial / expired. */
  isPaidCompany(): boolean {
    try {
      const user =
        this.tokenEncryptionService.getEncryptedItem<any>('user') ||
        this.tokenEncryptionService.getEncryptedItem<any>('logedInUser');
      return String(user?.meta?.quota_status || '').toLowerCase() === 'active';
    } catch {
      return false;
    }
  }

  continueLater(): void {
    this.markConnectWaitLeft('continue_later');
    const step = this.getState().currentStep;
    if (this.isPaidCompany()) {
      this.completePaidContinueLater(step);
      return;
    }
    this.trackEvent('continued_later', { step });
    this.patch({
      continuedLater: true,
      // Explicit pause must win over a stale finished/skipped flag from an older run.
      finished: false,
      skipped: false,
      journeyStatus: 'paused',
      // Keep paused (not private_active) so Continue later still lands in the real admin.
      // The floating explore card is shown from paused state, not this flag.
      privateEvaluationActive: false,
      evaluationStatus: 'inactive',
      installRecoveryBranch: 'continue_later',
      pausedAtStep: step,
      pausedAt: new Date().toISOString(),
      resumeDestination: resumeDestinationForStep(step),
      waitingReason: waitingReasonForStep(step),
    });
  }

  /**
   * Paid companies opened Journey from the menu. Continue later is a full exit —
   * do not pause, resume-bar, or pull them back into /setup.
   */
  private completePaidContinueLater(step: ActivationStepId): void {
    this.trackEvent('continued_later', { step, paidExit: true });
    this.trackEvent('activation_journey_finished', { source: 'continue_later_paid' });
    this.patch({
      finished: true,
      skipped: false,
      continuedLater: false,
      journeyStatus: 'completed',
      evaluationStatus: 'complete',
      waitingReason: null,
      pausedAtStep: null,
      pausedAt: null,
      resumeDestination: null,
      privateEvaluationActive: false,
      installRecoveryBranch: 'continue_later',
    });
    try {
      localStorage.setItem('supersee_onboarding_completed', 'true');
    } catch {
      /* ignore */
    }
  }

  clearContinueLater(): void {
    // Keep private evaluation resumable — do not wipe pause while evaluating.
    if (this.getState().privateEvaluationActive || this.getState().evaluationStatus === 'private_active') {
      return;
    }
    this.patch({
      continuedLater: false,
      journeyStatus: this.getState().finished
        ? 'completed'
        : this.getState().skipped
          ? 'skipped'
          : 'active',
      waitingReason: null,
      pausedAtStep: null,
      pausedAt: null,
      resumeDestination: null,
    });
  }

  skipJourney(): void {
    this.trackEvent('activation_journey_skipped', { step: this.getState().currentStep });
    this.patch({
      skipped: true,
      continuedLater: false,
      journeyStatus: 'skipped',
      waitingReason: null,
      pausedAtStep: null,
      pausedAt: null,
      privateEvaluationActive: false,
    });
  }

  /** Whether guard should nudge user back to /setup. */
  shouldRedirectToSetup(): boolean {
    if (!this.isEnabled) {
      return false;
    }
    const state = this.getState();
    if (
      state.journeyStatus === 'completed' ||
      state.journeyStatus === 'skipped' ||
      state.journeyStatus === 'paused' ||
      state.evaluationStatus === 'private_active' ||
      state.finished ||
      state.skipped
    ) {
      return false;
    }
    // First hire already exists — collecting is not unfinished setup.
    if (state.firstEmployeeId) {
      return false;
    }
    // Only resume an in-progress journey — do not force every login into /setup.
    return (
      state.completedSteps.length > 0 ||
      !!state.goal ||
      state.modeEducationSeen ||
      !!state.privateTestEmployeeId ||
      !!state.firstEmployeeId
    );
  }

  isFinishedOrSkipped(): boolean {
    const state = this.getState();
    return (
      state.journeyStatus === 'completed' ||
      state.journeyStatus === 'skipped' ||
      !!(state.finished || state.skipped)
    );
  }

  /**
   * Classic “all employees are Invited” install coach. Continue later and private
   * evaluation already have their own companion — do not dim the admin again.
   */
  shouldSuppressInviteFocusCoach(): boolean {
    if (!this.isEnabled || this.isFinishedOrSkipped()) {
      return false;
    }
    const state = this.getState();
    return (
      state.journeyStatus === 'paused' ||
      (!!state.continuedLater && !state.finished) ||
      state.privateEvaluationActive ||
      state.evaluationStatus === 'private_active'
    );
  }

  goToStep(stepId: ActivationStepId): void {
    let target = stepId;
    // Never re-open Access after ack / first completion — advance to Announce or Invite.
    if (target === 'setup_confirmed' && this.shouldSkipFairSetup()) {
      this.completeStep('setup_confirmed');
      target = shouldSkipAnnounce(this.getState()) ? 'first_employee' : 'announce';
    }
    if (target === 'announce' && shouldSkipAnnounce(this.getState())) {
      this.completeStep('announce');
      target = 'first_employee';
    }
    this.patch({
      currentStep: target,
      continuedLater: false,
      journeyStatus:
        this.getState().journeyStatus === 'completed' || this.getState().journeyStatus === 'skipped'
          ? this.getState().journeyStatus
          : 'active',
      waitingReason: null,
      pausedAtStep: null,
      pausedAt: null,
      resumeDestination: null,
    });
  }

  goBack(): void {
    const step = this.getState().currentStep;
    if (step === 'plan_viewed') {
      this.goToStep('goal_selected');
      return;
    }
    if (step === 'collection') {
      this.goToStep('plan_viewed');
      return;
    }
    if (step === 'setup_confirmed') {
      this.goToStep('collection');
      return;
    }
    if (step === 'announce') {
      this.patch({ wantsEmployeeInvite: false });
      this.goToStep('first_employee');
      return;
    }
    if (step === 'mode_education' || step === 'private_test') {
      this.goToStep('first_employee');
      return;
    }
    if (step === 'private_proof') {
      this.goToStep('private_test');
      return;
    }
    if (step === 'first_employee') {
      if (this.getState().wantsEmployeeInvite) {
        this.goToStep('announce');
        return;
      }
      this.goToStep('collection');
      return;
    }
    if (step === 'first_day_review') {
      this.goToStep('first_employee');
      return;
    }
    const idx = this.getStepIndex();
    if (idx > 0) {
      this.goToStep(ACTIVATION_STEPS[idx - 1].id);
    }
  }

  completeStep(stepId: ActivationStepId): void {
    const state = this.getState();
    if (state.completedSteps.includes(stepId)) {
      return;
    }
    this.patch({ completedSteps: [...state.completedSteps, stepId] });
  }

  isStepCompleted(stepId: ActivationStepId): boolean {
    return this.getState().completedSteps.includes(stepId);
  }

  reset(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.stateSubject.next(defaultState());
  }
}
